import type {
	addedNodeMutation,
	attributeMutation,
	eventWithTime,
	mutationData,
	customEvent as rrwebCustomEvent,
} from "@rrweb/types";
import {
	pack as bundledPack,
	record as bundledRecord,
} from "rrweb/es/rrweb/packages/rrweb/src/entries/all.js";
import type {
	ContractStatus,
	QuotaStatus,
	SessionAttribution,
	SessionFilters,
	SessionInfo,
	SessionTag,
	SessionTagsPayload,
	TrackCustomEventPayload,
	UserIdentifier,
	WebCoreClient,
	WebCoreOptions,
} from "./types";
import {
	installVisibilityObserver,
	tagSubtree,
	tagSubtreeIdle,
} from "./visibility-tagger";

interface IwJsErrorPayload {
	message: string;
	type: string;
	filename?: string;
	lineno?: number;
	colno?: number;
	stack?: string;
}

interface IwRequestErrorPayload {
	url: string;
	method: string;
	status: number;
	statusText?: string;
	duration_ms: number;
	response_preview?: string;
}

type IwCustomEvent<T> = rrwebCustomEvent<T> & { timestamp: number };

interface IwXhr extends XMLHttpRequest {
	__iw_method?: string;
	__iw_url?: string;
}

type RrwebSerializedNode = NonNullable<addedNodeMutation["node"]>;
type RrwebSerializedNodeWithMeta = RrwebSerializedNode & {
	isSVG?: boolean;
	textContent?: string | null;
	attributes?: Record<string, unknown>;
	childNodes?: RrwebSerializedNodeWithMeta[];
	tagName?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

const compactOptions = (options: WebCoreOptions): WebCoreOptions => {
	return Object.fromEntries(
		Object.entries(options).filter(([, value]) => value !== undefined),
	) as WebCoreOptions;
};

const getMutationData = (event: eventWithTime | null): mutationData | null => {
	if (!event || event.type !== 3) return null;
	const data = event.data as mutationData | undefined;
	if (!data || data.source !== 0) return null;
	return data;
};

declare const __INFINITEWATCH_SDK_VERSION__: string | undefined;
const LIB_VERSION =
	typeof __INFINITEWATCH_SDK_VERSION__ !== "undefined"
		? __INFINITEWATCH_SDK_VERSION__
		: "0.0.0";

const EXCLUDED_ORGANIZATIONS: Record<string, string> = {
	// Add organizations here as needed:
	// EXAMPLE_ORG: 'organization_id_here',
};

const EXCLUDED_ORG_IDS = new Set(Object.values(EXCLUDED_ORGANIZATIONS));

/**
 * CSS class injected onto DOM elements that should have their text masked in "All" mode
 * when unmask-selector rules are present. Using DOM class injection instead of
 * maskTextSelector "*:not(sel)" because rrweb's needMaskingText calls el.closest(selector),
 * which always finds a non-matching ancestor (e.g. <body>), making :not() exemptions useless.
 */
const UNMASK_CSS_CLASS = "iw-text-mask";

/**
 * Annotate the DOM with UNMASK_CSS_CLASS on elements that should have their text masked,
 * skipping elements inside unmask subtrees. Returns a cleanup function that removes the
 * class and disconnects the MutationObserver.
 */
function applyAllModeTextMasking(
	doc: Document,
	unmaskSelectors: string[],
): () => void {
	const isInUnmaskSubtree = (el: Element): boolean =>
		unmaskSelectors.some((sel) => {
			try {
				return el.matches(sel) || !!el.closest(sel);
			} catch {
				return false;
			}
		});

	const hasUnmaskDescendant = (el: Element): boolean =>
		unmaskSelectors.some((sel) => {
			try {
				return el.querySelector(sel) !== null;
			} catch {
				return false;
			}
		});

	const annotate = (el: Element) => {
		if (isInUnmaskSubtree(el)) return;
		// Don't add class to elements that contain unmask children — closest() on those
		// children would walk up and find this ancestor, masking them unintentionally.
		if (!hasUnmaskDescendant(el)) {
			for (const child of el.childNodes) {
				if (
					child.nodeType === 3 /* TEXT_NODE */ &&
					(child.textContent || "").trim()
				) {
					(el as HTMLElement).classList.add(UNMASK_CSS_CLASS);
					break;
				}
			}
		}
		for (const child of Array.from(el.children)) {
			annotate(child);
		}
	};

	annotate(doc.documentElement);

	const removeMaskClasses = (el: Element) => {
		if (el.classList.contains(UNMASK_CSS_CLASS)) {
			(el as HTMLElement).classList.remove(UNMASK_CSS_CLASS);
		}
		el.querySelectorAll(`.${UNMASK_CSS_CLASS}`).forEach((child) => {
			(child as HTMLElement).classList.remove(UNMASK_CSS_CLASS);
		});
	};

	const observer = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			if (mutation.type === "childList") {
				for (const node of Array.from(mutation.addedNodes)) {
					if ((node as Node).nodeType === 1 /* ELEMENT_NODE */) {
						const el = node as Element;
						if (isInUnmaskSubtree(el)) {
							// Node was moved into an unmask subtree — strip any stale mask classes
							// that were applied when it lived outside the unmask zone.
							removeMaskClasses(el);
						} else {
							annotate(el);
						}
					}
				}
			}
		}
	});

	observer.observe(doc.body || doc.documentElement, {
		childList: true,
		subtree: true,
	});

	return () => {
		observer.disconnect();
		doc.querySelectorAll(`.${UNMASK_CSS_CLASS}`).forEach((el) => {
			(el as HTMLElement).classList.remove(UNMASK_CSS_CLASS);
		});
	};
}

const DEFAULT_CONFIG = {
	baseUrl: "https://api.infinitewatch.ai",
	endpoint: "https://ingest.infinitewatch.ai/v1/ingest",
	endpointInsights: "https://api.infinitewatch.ai",
	endpointConfig: "https://ingest.infinitewatch.ai/v1/ingest/config",
	endpointErrors: "https://iw-backend-live.fly.dev/v1/ingest/errors",
	sessionId: null as string | null,
	userId: null as string | null,
	organizationId: null as string | null,
	debug: false,
	defaultSamplingPercent: 100,
	maskAllInputs: true,
	maskInputOptions: {
		password: true,
		email: true,
		text: true,
		tel: true,
		url: true,
		textarea: true,
		select: true,
		checkbox: true,
		radio: true,
		file: true,
	} as Record<string, boolean>,
	blockClass: "",
	blockSelector: "",
	maskTextSelector: "",
	maskTextClass: "",
	maskingRules: [] as Array<{ selector: string; mode: "mask" | "unmask" }>,
	maskSelectors: [] as string[],
	recordIframe: false,
	recordCanvas: false,
	recordCrossOriginIframes: false,
	sessionStorageKey: "iw_session",
	autoResumeSession: true,
	batchSize: 200,
	flushInterval: 10_000,
	minSessionDuration: 5_000,
	sessionTimeout: 30 * 60 * 1000,
	sessionHeartbeatInterval: null as number | null,
	refresh: false,
	inlineStylesheet: true,
	collectFonts: false,
	computeVisibility: false,
	generateInsights: false,
	blackList: ["yui_"],
	captureErrors: true,
	captureDataLayer: false,
	dataLayerName: "dataLayer",
	dataLayerAllowlist: [
		"event",
		"app_version",
		"app_name",
		"app_id",
		"release",
		"version",
		"build",
		"env",
		"environment",
	] as string[],
	sendBackoffMs: 1000,
	captureAttribution: true,
};

const IW_DATALAYER_TAG = "iw-datalayer";
/** Cap each captured payload at ~2KB to prevent runaway dataLayer pushes from bloating the recording. */
const IW_DATALAYER_MAX_PAYLOAD_BYTES = 2048;

const BYTES_IN_KILOBYTE = 1024;

const KEEPALIVE_MAX_BYTES = 63 * BYTES_IN_KILOBYTE;

const REQ_ERROR_RATE_MS = 5_000;
const RATE_LIMIT_MAP_MAX = 200;

/** TTL (in days) for the persisted session cookie. ~6 months keeps a returning visitor recognizable while still expiring stale entries. */
const SESSION_COOKIE_TTL_DAYS = 182;
const SESSION_COOKIE_TTL_MS = SESSION_COOKIE_TTL_DAYS * 24 * 60 * 60 * 1000;

/** Storage key for cross-session first-touch/last-touch attribution. */
const ATTRIBUTION_STORAGE_KEY = "iw_attribution";
/** Attribution values ride in a cookie, so cap each one to keep the cookie small. */
const ATTRIBUTION_MAX_VALUE_LENGTH = 300;
const UTM_KEYS = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_content",
	"utm_term",
] as const;

const URL_PARAMS = {
	VERSION: "v",
	REQUEST_TYPE: "t",
	KEEPALIVE: "k",
	SIZE: "s",
	COMPRESSION: "c",
	SESSION_ID: "i",
	SDK_NAME: "n",
};

type SessionData = {
	sessionId: string | null;
	userId: string | null;
	organizationId: string | null;
	lastActivity: number;
	sessionStartTime: number | null;
	isActive: boolean;
	config: {
		endpoint: string;
		sessionTimeout: number;
	};
	externalId?: string | null;
	expiresAt?: number;
	attribution?: SessionAttribution;
};

type TouchAttribution = SessionAttribution & { ts?: number };

/** Cross-session attribution persisted under ATTRIBUTION_STORAGE_KEY: first_touch is written once, last_touch on every new campaign entry. */
type UserAttribution = {
	first_touch?: TouchAttribution;
	last_touch?: TouchAttribution;
};

/** Stub returned when storage has no sessionId/lastActivity (e.g. after expiry/clear); used to carry externalId/organizationId into next session */
type SessionStub = {
	externalId?: string | null;
	organizationId?: string | null;
	expiresAt?: number;
};

type CompressionResult = {
	body: BodyInit;
	compressed: boolean;
	originalSize: number;
};

const getWindow = () => {
	if (typeof window !== "undefined") {
		return window as Window;
	}
	if (typeof globalThis !== "undefined" && "window" in globalThis) {
		// biome-ignore lint/suspicious/noExplicitAny: globalThis typing for non-standard environments
		return (globalThis as any).window as Window;
	}
	const doc =
		typeof document !== "undefined"
			? (document as Document)
			: typeof globalThis !== "undefined" && "document" in globalThis
				? // biome-ignore lint/suspicious/noExplicitAny: globalThis typing for non-standard environments
					((globalThis as any).document as Document)
				: undefined;
	return doc?.defaultView ?? undefined;
};

const getDocument = () => {
	if (typeof document !== "undefined") {
		return document as Document;
	}
	if (typeof globalThis !== "undefined" && "document" in globalThis) {
		// biome-ignore lint/suspicious/noExplicitAny: globalThis typing for non-standard environments
		return (globalThis as any).document as Document;
	}
	return undefined;
};

const isBrowser = () => !!(getWindow() && getDocument());

const now = () => Date.now();

const getCrypto = () => {
	const win = getWindow();
	if (win?.crypto) {
		return win.crypto;
	}
	if (typeof globalThis !== "undefined" && "crypto" in globalThis) {
		return globalThis.crypto;
	}
	return undefined;
};

const randomString = (length: number) => {
	const cryptoApi = getCrypto();
	if (!cryptoApi) {
		// Not expected in supported browsers, but keeps ID generation functional in odd environments.
		return `${now().toString(36)}${"0".repeat(length)}`.slice(0, length);
	}
	const alphabet = "0123456789abcdefghijklmnopqrstuvwxyz";
	const bytes = new Uint8Array(length);
	cryptoApi.getRandomValues(bytes);
	let result = "";
	for (let i = 0; i < bytes.length; i += 1) {
		const byte = bytes[i] ?? 0;
		result += alphabet[byte % alphabet.length];
	}
	return result;
};

const isOrganizationExcluded = (orgId?: string | null) => {
	return !!orgId && EXCLUDED_ORG_IDS.has(orgId);
};

const applyBaseUrlToOptions = (options: WebCoreOptions): WebCoreOptions => {
	const compactedOptions = compactOptions(options);
	const baseUrl = compactedOptions.baseUrl?.trim().replace(/\/+$/, "");
	if (!baseUrl) {
		return { ...compactedOptions };
	}
	const nextOptions: WebCoreOptions = { ...compactedOptions, baseUrl };
	if (nextOptions.endpoint == null) {
		nextOptions.endpoint = `${baseUrl}/v1/ingest`;
	}
	if (nextOptions.endpointConfig == null) {
		nextOptions.endpointConfig = `${baseUrl}/v1/ingest/config`;
	}
	return nextOptions;
};

export const createWebCore = (sdkName: string = "web-core"): WebCoreClient => {
	let config = { ...DEFAULT_CONFIG };
	let isInitialized = false;
	let eventBuffer: unknown[] = [];
	let flushTimer: ReturnType<typeof setInterval> | null = null;
	let minSessionFlushTimer: ReturnType<typeof setTimeout> | null = null;
	let snapshotFlushTimeout: ReturnType<typeof setTimeout> | null = null;
	let stopRecording: ReturnType<NonNullable<Window["rrweb"]>["record"]> | null =
		null;
	let textMaskCleanup: (() => void) | null = null;
	let allModeUnmaskSelectors: string[] = [];
	let sessionId: string | null = null;
	let userId: string | null = null;
	let externalId: string | null = null;
	let organizationId: string | null = null;
	let sessionHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
	let sessionExpiryTimer: ReturnType<typeof setTimeout> | null = null;
	let isSessionActive = false;
	let sessionStartTime: number | null = null;
	let isSampledIn = true;
	let hardStop = false;
	let transportBlocked = false;
	let contractStatus: ContractStatus = null;
	let quotaStatus: QuotaStatus = null;
	let lastRecordedUrl = "";
	// Entry attribution for the current session — captured once when a new
	// sessionId is created and kept stable across page loads via storage.
	let sessionAttribution: SessionAttribution | null = null;
	let urlChangeCleanup: (() => void) | null = null;
	let dynamicStyleObserver: MutationObserver | null = null;
	let visibilityTagCleanup: (() => void) | null = null;
	let errorCaptureCleanup: (() => void) | null = null;
	let docSizeCleanup: (() => void) | null = null;
	// Tracks the last { width, height } emitted by the iw-doc-size custom event
	// for the current session. Recurring triggers (observer / poll) compare
	// against this baseline; anchor triggers (start / route) also overwrite it
	// so the next recurring check measures against the freshest dimensions.
	let lastEmittedDocSize: { width: number; height: number } | null = null;
	let dataLayerCaptureCleanup: (() => void) | null = null;
	let sessionFilters: SessionFilters | null = null;
	let awaitingIdentify = false;
	let pendingIdentify: UserIdentifier | null = null;
	let pendingSessionTags: SessionTagsPayload[] = [];
	let pendingCustomEvents: TrackCustomEventPayload[] = [];
	let urlFilterPaused = false;
	let filterUrlCleanup: (() => void) | null = null;
	let firstRrwebEventType: number | null = null;
	let fullSnapshotSeen = false;
	type FullSnapshotFilteredReason =
		| "none"
		| "idle_filter"
		| "start_recording_failed"
		| "rrweb_never_emitted"
		| "rrweb_emit_no_snapshot";
	let fullSnapshotFilteredReason: FullSnapshotFilteredReason = "none";
	let ingestAttemptsCount = 0;
	let eventsSentCount = 0;
	let firstIngestAttemptLogged = false;
	let captureDiagnosticsEmitted = false;
	// Observability extras — set per-session, cleared in resetCaptureDiagnosticsState
	let idleMsWhenSnapshotDropped: number | null = null; // how stale __iw_lastUserIntentAt was when idle_filter dropped the full snapshot
	let pageHiddenAtStart: boolean | null = null; // document.hidden at the moment startRecording was called
	let isSessionRestart = false; // true when this session was created by an in-page timeout restart (not a fresh page load)
	let startRecordingError: string | null = null; // "<name>: <message>" captured from startRecording catch — null on the happy path
	// === Snapshot-pipeline instrumentation ============================================
	// These fields track each step a rrweb type-2 full snapshot goes through inside the
	// SDK so that, when a session ends up unreplayable despite full_snapshot_seen=true,
	// we can pinpoint exactly where the snapshot was lost (rrweb emit / filter / pack /
	// buffer push / network send). Reset together with the rest of diagnostic state.
	let snapshotEmitCount = 0; // how many type-2 events rrweb's emit fired (>=2 = ran twice, e.g. on session restart)
	type SnapshotFilterResult = "passed" | "dropped_idle" | "dropped_other";
	let snapshotFilterResult: SnapshotFilterResult | null = null; // outcome of filterEvent for the most recent type-2
	let snapshotRebuildError: string | null = null; // error from rebuildInteractiveIdMapFromFullSnapshot
	let snapshotPackOk: boolean | null = null; // packFn succeeded for the snapshot
	let snapshotPackError: string | null = null; // error from packFn (zlib failure, encoding issue, etc.)
	type SnapshotBufferResult =
		| "pushed"
		| "dropped_hardstop"
		| "dropped_url_filter"
		| "dropped_restart"
		| "dropped_inactive";
	let snapshotBufferResult: SnapshotBufferResult | null = null; // outcome of addEvent for the snapshot
	let snapshotBufferTimingMs: number | null = null; // ms between emit and addEvent resolving (high = race)
	let snapshotIngestStatus: number | null = null; // HTTP status code of the chunk containing the snapshot
	let snapshotIngestOk: boolean | null = null; // whether that chunk was acknowledged
	let snapshotPendingIngest = false; // internal: snapshot is buffered, waiting for next sendChunk
	let snapshotTransport: string | null = null; // which transport carried the snapshot chunk ("keepalive"/"beacon"/"fetch", "_blocked" suffix when the request got no HTTP response). Distinguishes a real 403 (reached an HTTP server) from a network/proxy block (status 0).
	// Latch + watchdog so the diagnostic still fires when rrweb never emits a snapshot
	// or when startRecording throws (isSessionActive gets unset before we can emit).
	let diagnosticArmed = false;
	const CAPTURE_DIAGNOSTICS_WATCHDOG_MS = 10_000;
	let captureDiagnosticsWatchdog: ReturnType<typeof setTimeout> | null = null;
	const clearCaptureDiagnosticsWatchdog = () => {
		if (captureDiagnosticsWatchdog) {
			clearTimeout(captureDiagnosticsWatchdog);
			captureDiagnosticsWatchdog = null;
		}
	};
	// Follow-up beacon: closes the blind spot where the 10s watchdog fires BEFORE the
	// snapshot's first send resolves. In that case the primary beacon reports
	// snapshot_ingest_status=null, so the snapshot's real outcome (403/413/0/timeout)
	// would never be reported. The follow-up fires at most once, only for that
	// population — zero extra beacons on the happy path (send resolves before 10s).
	const CAPTURE_DIAGNOSTICS_FOLLOWUP_MS = 20_000; // fallback (> the 20s fetch timeout) so a snapshot pending at watchdog time still emits even if it is never sent
	let snapshotPendingAtWatchdog = false;
	let snapshotFollowupEmitted = false;
	let snapshotFollowupTimer: ReturnType<typeof setTimeout> | null = null;
	const clearCaptureDiagnosticsFollowup = () => {
		if (snapshotFollowupTimer) {
			clearTimeout(snapshotFollowupTimer);
			snapshotFollowupTimer = null;
		}
	};

	const resetCaptureDiagnosticsState = () => {
		firstRrwebEventType = null;
		fullSnapshotSeen = false;
		fullSnapshotFilteredReason = "none";
		ingestAttemptsCount = 0;
		eventsSentCount = 0;
		firstIngestAttemptLogged = false;
		captureDiagnosticsEmitted = false;
		idleMsWhenSnapshotDropped = null;
		pageHiddenAtStart = null;
		isSessionRestart = false;
		startRecordingError = null;
		snapshotEmitCount = 0;
		snapshotFilterResult = null;
		snapshotRebuildError = null;
		snapshotPackOk = null;
		snapshotPackError = null;
		snapshotBufferResult = null;
		snapshotBufferTimingMs = null;
		snapshotIngestStatus = null;
		snapshotIngestOk = null;
		snapshotPendingIngest = false;
		snapshotTransport = null;
		snapshotPendingAtWatchdog = false;
		snapshotFollowupEmitted = false;
		diagnosticArmed = false;
		clearCaptureDiagnosticsWatchdog();
		clearCaptureDiagnosticsFollowup();
	};

	const log = (message: string, ...args: unknown[]) => {
		if (config.debug) {
			console.log(
				"[InfiniteWatch]",
				new Date().toISOString(),
				message,
				...args,
			);
		}
	};

	const error = (message: string, ...args: unknown[]) => {
		const now = new Date();
		if (config.debug) {
			console.error(
				"[InfiniteWatch]",
				now.toISOString(),
				"ERROR:",
				message,
				...args,
			);
			if (args.length > 0 && args[0] instanceof Error) {
				console.error("[InfiniteWatch] Stack trace:", args[0].stack);
			}
		}

		// Best effort, light client-side error notifications for tracking browser/network issues
		if (isBrowser() && navigator.sendBeacon) {
			const body = new Blob([
				JSON.stringify({
					timestamp: now.toISOString(),
					message,
					organization_id: organizationId,
					session_id: sessionId,
					// Deliberately ignoring args, we just want small quick info that errors are happening, not the full stack trace
				}),
			]);

			const endpointUrl = buildEndpointUrl(
				config.endpointErrors,
				"b",
				null,
				body.size / BYTES_IN_KILOBYTE,
				false,
				sessionId,
			);

			navigator.sendBeacon(endpointUrl, body);
		}
	};

	const parseSessionFilters = (
		raw: Record<string, unknown>,
	): SessionFilters => {
		const validAuthFilters = ["any", "signed_in", "anonymous"] as const;
		const authFilter = validAuthFilters.includes(
			raw.auth_filter as (typeof validAuthFilters)[number],
		)
			? (raw.auth_filter as SessionFilters["auth_filter"])
			: "any";
		return {
			ignore_internal_users: Boolean(raw.ignore_internal_users),
			internal_domains: Array.isArray(raw.internal_domains)
				? raw.internal_domains
				: [],
			auth_filter: authFilter,
			include_urls: Array.isArray(raw.include_urls) ? raw.include_urls : [],
			exclude_urls: Array.isArray(raw.exclude_urls) ? raw.exclude_urls : [],
			bounce_urls: Array.isArray(raw.bounce_urls) ? raw.bounce_urls : [],
		};
	};

	const currentPathname = (): string => {
		try {
			const win = getWindow();
			return win?.location?.pathname || "/";
		} catch {
			return "/";
		}
	};

	const pathMatchesPattern = (path: string, pattern: string): boolean => {
		if (!path || !pattern) return false;
		const normalized = pattern.endsWith("/") ? pattern : `${pattern}/`;
		return path === pattern || path.startsWith(normalized);
	};

	const isUrlIncluded = (): boolean => {
		if (!sessionFilters?.include_urls?.length) return true;
		const path = currentPathname();
		return sessionFilters.include_urls.some((p) => pathMatchesPattern(path, p));
	};

	const isUrlExcluded = (): boolean => {
		if (!sessionFilters?.exclude_urls?.length) return false;
		const path = currentPathname();
		return sessionFilters.exclude_urls.some((p) => pathMatchesPattern(path, p));
	};

	const isInternalEmail = (email: string | undefined): boolean => {
		if (
			!email ||
			!sessionFilters?.ignore_internal_users ||
			!sessionFilters.internal_domains?.length
		)
			return false;
		const lower = email.toLowerCase();
		return sessionFilters.internal_domains.some((d) =>
			lower.endsWith(d.toLowerCase()),
		);
	};

	const setupUrlFilterMonitoring = () => {
		if (filterUrlCleanup) return;
		const win = getWindow();
		if (!win) return;

		const check = () => {
			if (hardStop || !isInitialized) return;

			// Exclude always wins — pause recording but allow resuming on navigation
			if (isUrlExcluded()) {
				if (isSessionActive) {
					log("URL excluded by session filter, pausing recording");
					urlFilterPaused = true;
					stopRecordingSession();
				}
				return;
			}

			// Resumed from excluded URL or include now matches — start recording
			if (
				!isSessionActive &&
				!awaitingIdentify &&
				(urlFilterPaused ||
					(sessionFilters?.include_urls?.length && isUrlIncluded()))
			) {
				urlFilterPaused = false;
				ensureSessionContext();
				if (!isSampledIn) {
					return;
				}
				log("URL now matches filter, starting recording");
				startRecording().catch((err) =>
					error("Failed to start recording after URL match:", err),
				);
			}
		};

		const origPushState = win.history.pushState?.bind(win.history);
		const origReplaceState = win.history.replaceState?.bind(win.history);

		if (origPushState) {
			win.history.pushState = (...args: Parameters<typeof origPushState>) => {
				origPushState(...args);
				check();
			};
		}
		if (origReplaceState) {
			win.history.replaceState = (
				...args: Parameters<typeof origReplaceState>
			) => {
				origReplaceState(...args);
				check();
			};
		}

		const onPopState = () => check();
		win.addEventListener("popstate", onPopState);

		filterUrlCleanup = () => {
			if (origPushState) win.history.pushState = origPushState;
			if (origReplaceState) win.history.replaceState = origReplaceState;
			win.removeEventListener("popstate", onPopState);
			filterUrlCleanup = null;
		};
	};

	const generateSessionId = () => {
		const nextId = `session_${now()}_${randomString(9)}`;
		log("Generated new session ID:", nextId);
		return nextId;
	};

	const generateUserId = () => {
		const nextId = `user_${randomString(9)}`;
		log("Generated new user ID:", nextId);
		return nextId;
	};

	const getSamplingPercentForOrg = (orgId?: string | null) => {
		if (!orgId) {
			return typeof config.defaultSamplingPercent === "number"
				? config.defaultSamplingPercent
				: 50;
		}
		const base =
			typeof config.defaultSamplingPercent === "number"
				? config.defaultSamplingPercent
				: 50;
		return Math.max(0, Math.min(100, base));
	};

	const hashStringToPercent = (value: string) => {
		let hash = 5381;
		const input = String(value);
		for (let i = 0; i < input.length; i += 1) {
			hash = (hash << 5) + hash + input.charCodeAt(i);
			hash |= 0;
		}
		return Math.abs(hash) % 100;
	};

	const shouldSample = (orgId: string | null, sessId: string | null) => {
		const percent = getSamplingPercentForOrg(orgId);
		const bucket = hashStringToPercent(sessId || "");
		return bucket < percent;
	};

	const compressGzip = async (data: string): Promise<CompressionResult> => {
		if (typeof CompressionStream !== "undefined") {
			try {
				const originalSize = new Blob([data]).size;
				const stream = new Blob([data]).stream();
				const compressedStream = stream.pipeThrough(
					new CompressionStream("gzip"),
				);
				const compressedBlob = await new Response(compressedStream).blob();
				return { body: compressedBlob, compressed: true, originalSize };
			} catch (err) {
				log("Compression failed, sending uncompressed:", err);
				const fallbackBlob = new Blob([data]);
				return {
					body: fallbackBlob,
					compressed: false,
					originalSize: fallbackBlob.size,
				};
			}
		}
		log("CompressionStream not available, sending uncompressed");
		const fallbackBlob = new Blob([data]);
		return {
			body: fallbackBlob,
			compressed: false,
			originalSize: fallbackBlob.size,
		};
	};

	const getCookieDomain = () => {
		const win = getWindow();
		if (!win) return "";
		const hostname = win.location.hostname;
		if (
			hostname === "localhost" ||
			hostname === "127.0.0.1" ||
			/^\d+\.\d+\.\d+\.\d+$/.test(hostname)
		) {
			return "";
		}
		const parts = hostname.split(".");
		if (parts.length >= 2) {
			return `.${parts.slice(-2).join(".")}`;
		}
		return hostname;
	};

	const getCookieSecurityAttrs = () => {
		return "; Secure; SameSite=None";
	};

	const setCookie = (name: string, value: string, days = 365): boolean => {
		const doc = getDocument();
		if (!doc) return false;
		try {
			const expires = new Date();
			expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
			const encodedValue = encodeURIComponent(value);
			const domain = getCookieDomain();
			const domainAttr = domain ? `; Domain=${domain}` : "";
			const securityAttrs = getCookieSecurityAttrs();
			const cookieBase = `${name}=${encodedValue}`;
			const cookieWithAttrs = `${cookieBase}; expires=${expires.toUTCString()}; Path=/${domainAttr}${securityAttrs}`;
			doc.cookie = cookieWithAttrs;
			if (!doc.cookie.includes(`${name}=`)) {
				doc.cookie = cookieBase;
			}
			return doc.cookie.includes(`${name}=`);
		} catch {
			return false;
		}
	};

	const getCookie = (name: string) => {
		const doc = getDocument();
		if (!doc) return null;
		try {
			const nameEQ = `${name}=`;
			const ca = doc.cookie.split(";");
			for (let i = 0; i < ca.length; i += 1) {
				let c = ca[i] ?? "";
				while (c.charAt(0) === " ") c = c.substring(1, c.length);
				if (c.indexOf(nameEQ) === 0) {
					return decodeURIComponent(c.substring(nameEQ.length, c.length));
				}
			}
		} catch {
			return null;
		}
		return null;
	};

	const deleteCookie = (name: string) => {
		const doc = getDocument();
		if (!doc) return;
		try {
			const domain = getCookieDomain();
			const domainAttr = domain ? `; Domain=${domain}` : "";
			const securityAttrs = getCookieSecurityAttrs();
			doc.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; Path=/${domainAttr}${securityAttrs}`;
			doc.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; Path=/`;
		} catch {
			/* ignore */
		}
	};

	const getLocalStorage = (): Storage | null => {
		try {
			return getWindow()?.localStorage ?? null;
		} catch {
			return null;
		}
	};

	const getPersistedSession = (name: string) => {
		const cookieValue = getCookie(name);
		if (cookieValue) return cookieValue;
		try {
			return getLocalStorage()?.getItem(name) ?? null;
		} catch {
			return null;
		}
	};

	const setPersistedSession = (name: string, value: string) => {
		const cookieSaved = setCookie(name, value, SESSION_COOKIE_TTL_DAYS);
		let localSaved = false;
		try {
			const storage = getLocalStorage();
			if (storage) {
				storage.setItem(name, value);
				localSaved = true;
			}
		} catch {
			localSaved = false;
		}
		if (!cookieSaved && !localSaved) {
			log("Session persistence unavailable; cookies/localStorage blocked");
		}
	};

	const deletePersistedSession = (name: string) => {
		deleteCookie(name);
		try {
			getLocalStorage()?.removeItem(name);
		} catch {
			/* ignore */
		}
	};

	const cleanAttributionValue = (value: unknown): string | null => {
		if (typeof value !== "string") return null;
		const trimmed = value.trim();
		if (!trimmed) return null;
		return trimmed.slice(0, ATTRIBUTION_MAX_VALUE_LENGTH);
	};

	const captureAttributionFromPage = (): SessionAttribution => {
		const attribution: SessionAttribution = {};
		const win = getWindow();
		const doc = getDocument();
		const location = win?.location;
		const setField = (key: keyof SessionAttribution, value: unknown) => {
			const cleaned = cleanAttributionValue(value);
			if (cleaned) attribution[key] = cleaned;
		};
		setField("entry_url", location?.href);
		setField("pathname", location?.pathname);
		setField("search", location?.search);
		setField("hash", location?.hash);
		setField("referrer", doc?.referrer);
		if (attribution.referrer) {
			try {
				setField("referrer_host", new URL(attribution.referrer).hostname);
			} catch {
				/* unparsable referrer — leave referrer_host unset */
			}
		}
		try {
			const params = new URLSearchParams(location?.search ?? "");
			for (const key of UTM_KEYS) {
				setField(key, params.get(key));
			}
		} catch {
			/* ignore malformed query string */
		}
		return attribution;
	};

	/** A campaign touch is any entry carrying UTM params or an external referrer. */
	const isCampaignTouch = (attribution: SessionAttribution): boolean => {
		if (UTM_KEYS.some((key) => attribution[key])) return true;
		const currentHost = getWindow()?.location?.hostname ?? "";
		return Boolean(
			attribution.referrer_host && attribution.referrer_host !== currentHost,
		);
	};

	const loadUserAttribution = (): UserAttribution | null => {
		try {
			const raw = getPersistedSession(ATTRIBUTION_STORAGE_KEY);
			if (!raw) return null;
			const parsed = JSON.parse(raw) as UserAttribution;
			if (!parsed || typeof parsed !== "object") return null;
			return parsed;
		} catch {
			return null;
		}
	};

	const updateUserAttribution = (
		attribution: SessionAttribution,
		{ allowLastTouch = true } = {},
	) => {
		try {
			const existing = loadUserAttribution() ?? {};
			let changed = false;
			if (!existing.first_touch) {
				existing.first_touch = { ...attribution, ts: Date.now() };
				changed = true;
			}
			if (allowLastTouch && isCampaignTouch(attribution)) {
				existing.last_touch = { ...attribution, ts: Date.now() };
				changed = true;
			}
			if (changed) {
				setPersistedSession(ATTRIBUTION_STORAGE_KEY, JSON.stringify(existing));
			}
		} catch {
			/* ignore — attribution persistence is best-effort */
		}
	};

	/**
	 * Capture entry attribution for a newly created session and fold it into
	 * the persisted cross-session first/last touch. `allowLastTouch: false` is
	 * used when adopting a legacy resumed session that predates attribution —
	 * the current page is mid-session, not a campaign entry.
	 */
	const beginNewSessionAttribution = ({ allowLastTouch = true } = {}) => {
		if (!config.captureAttribution) {
			sessionAttribution = null;
			return;
		}
		sessionAttribution = captureAttributionFromPage();
		updateUserAttribution(sessionAttribution, { allowLastTouch });
	};

	/** Flattened attribution fields added to the `session-started` timeline event. */
	const buildSessionStartedAttribution = (): Record<string, string> => {
		const fields: Record<string, string> = {};
		if (!config.captureAttribution) return fields;
		if (sessionAttribution) {
			for (const [key, value] of Object.entries(sessionAttribution)) {
				// pathname/search/hash already ride on the event as current-page values
				if (key === "pathname" || key === "search" || key === "hash") continue;
				if (value) fields[key] = value;
			}
		}
		const userAttribution = loadUserAttribution();
		for (const touch of ["first_touch", "last_touch"] as const) {
			const data = userAttribution?.[touch];
			if (!data) continue;
			for (const [key, value] of Object.entries(data)) {
				if (key === "pathname" || key === "search" || key === "hash") continue;
				if (key === "ts") {
					if (typeof value === "number") fields[`${touch}_ts`] = String(value);
					continue;
				}
				if (typeof value === "string" && value) {
					fields[`${touch}_${key === "entry_url" ? "url" : key}`] = value;
				}
			}
		}
		return fields;
	};

	const saveMinimalSessionToStorage = (data: SessionStub) => {
		const storageKey = config.sessionStorageKey || "iw_session";
		if (!storageKey) return;
		try {
			const payload: SessionStub = {};
			let hasPayload = false;
			if (data.externalId != null && data.externalId !== "") {
				payload.externalId = data.externalId;
				hasPayload = true;
			}
			if (data.organizationId != null && data.organizationId !== "") {
				payload.organizationId = data.organizationId;
				hasPayload = true;
			}
			if (!hasPayload) {
				deletePersistedSession(storageKey);
				return;
			}
			payload.expiresAt = now() + SESSION_COOKIE_TTL_MS;
			setPersistedSession(storageKey, JSON.stringify(payload));
		} catch (_) {
			/* ignore */
		}
	};

	const saveSessionToStorage = () => {
		const storageKey = config.sessionStorageKey || "iw_session";
		if (!storageKey) {
			log("No session storage key configured, skipping save");
			return;
		}
		const sessionData: SessionData = {
			sessionId,
			userId,
			organizationId,
			lastActivity: now(),
			sessionStartTime,
			isActive: isSessionActive,
			config: {
				endpoint: config.endpoint,
				sessionTimeout: config.sessionTimeout,
			},
			expiresAt: now() + SESSION_COOKIE_TTL_MS,
		};
		if (sessionAttribution && Object.keys(sessionAttribution).length > 0) {
			sessionData.attribution = sessionAttribution;
		}
		if (externalId) {
			sessionData.externalId = externalId;
		} else {
			try {
				const existing = getPersistedSession(storageKey);
				if (existing) {
					const parsed = JSON.parse(existing) as SessionStub &
						Partial<SessionData>;
					if (parsed?.externalId) {
						sessionData.externalId = parsed.externalId;
						externalId = parsed.externalId;
					}
				}
			} catch (_) {
				/* ignore */
			}
		}
		try {
			setPersistedSession(storageKey, JSON.stringify(sessionData));
		} catch (err) {
			error("Failed to save session to storage:", err);
		}
	};

	const clearSessionFromStorage = () => {
		const storageKey = config.sessionStorageKey || "iw_session";
		if (!storageKey) {
			log("No session storage key configured, skipping clear");
			return;
		}
		try {
			const existing = getPersistedSession(storageKey);
			if (existing) {
				const parsed = JSON.parse(existing) as SessionStub &
					Partial<SessionData>;
				if (
					(parsed?.externalId != null && parsed.externalId !== "") ||
					(parsed?.organizationId != null && parsed.organizationId !== "")
				) {
					saveMinimalSessionToStorage({
						externalId: parsed.externalId,
						organizationId: parsed.organizationId,
					});
					return;
				}
			}
			deletePersistedSession(storageKey);
		} catch (err) {
			error("Failed to clear session from storage:", err);
		}
	};

	const loadSessionFromStorage = (): SessionData | SessionStub | null => {
		const storageKey = config.sessionStorageKey || "iw_session";
		if (!storageKey) {
			log("No session storage key configured, skipping load");
			return null;
		}
		try {
			const raw = getPersistedSession(storageKey);
			if (!raw) return null;
			const parsed = JSON.parse(raw) as SessionData & SessionStub;
			if (parsed.expiresAt != null && parsed.expiresAt <= now()) {
				deletePersistedSession(storageKey);
				return null;
			}
			const hasSession =
				parsed.sessionId != null && parsed.lastActivity != null;
			if (!hasSession) {
				return {
					externalId: parsed.externalId,
					organizationId: parsed.organizationId,
					expiresAt: parsed.expiresAt,
				};
			}
			const timeSinceLastActivity = now() - (parsed.lastActivity ?? 0);
			const totalSessionDuration =
				parsed.sessionStartTime != null ? now() - parsed.sessionStartTime : 0;
			if (
				timeSinceLastActivity > config.sessionTimeout ||
				totalSessionDuration > config.sessionTimeout
			) {
				if (
					(parsed.externalId != null && parsed.externalId !== "") ||
					(parsed.organizationId != null && parsed.organizationId !== "")
				) {
					saveMinimalSessionToStorage({
						externalId: parsed.externalId,
						organizationId: parsed.organizationId,
					});
				} else {
					deletePersistedSession(storageKey);
				}
				return null;
			}
			return parsed as SessionData;
		} catch (err) {
			error("Failed to load session from storage:", err);
			clearSessionFromStorage();
			return null;
		}
	};

	const getOrCreateUserId = () => {
		const existingSession = loadSessionFromStorage();
		if (
			existingSession &&
			"userId" in existingSession &&
			existingSession.userId
		) {
			return existingSession.userId;
		}
		return generateUserId();
	};

	const updateSessionActivity = () => {
		if (isSessionActive) {
			saveSessionToStorage();
			if (config.refresh) {
				resetSessionExpiryTimer();
			}
		}
	};

	const startSessionHeartbeat = () => {
		if (sessionHeartbeatTimer) {
			clearInterval(sessionHeartbeatTimer);
		}

		// Always run a heartbeat to check for session expiry
		// Use configured interval or default to 60 seconds
		const checkInterval = config.sessionHeartbeatInterval || 60_000;

		sessionHeartbeatTimer = setInterval(() => {
			if (isSessionActive) {
				// Check if session has exceeded maximum duration
				if (sessionStartTime) {
					const totalDuration = now() - sessionStartTime;
					if (totalDuration > config.sessionTimeout) {
						log(
							`Session expired (exceeded max duration of ${config.sessionTimeout / 60_000} minutes), stopping session`,
						);
						stopRecordingSession();
						return;
					}
				}

				// Update or save session activity
				if (config.refresh) {
					updateSessionActivity();
				} else {
					saveSessionToStorage();
				}
			}
		}, checkInterval);
	};

	const stopSessionHeartbeat = () => {
		if (sessionHeartbeatTimer) {
			clearInterval(sessionHeartbeatTimer);
			sessionHeartbeatTimer = null;
		} else {
			log("No heartbeat timer to stop");
		}
	};

	const startSessionExpiryTimer = () => {
		if (sessionExpiryTimer) {
			clearTimeout(sessionExpiryTimer);
		}
		let timeoutDuration = config.sessionTimeout;
		if (sessionStartTime) {
			const elapsed = now() - sessionStartTime;
			const remaining = config.sessionTimeout - elapsed;
			if (remaining <= 0) {
				clearSessionFromStorage();
				sessionId = generateSessionId();
				sessionStartTime = now();
				beginNewSessionAttribution();
				timeoutDuration = config.sessionTimeout;
			} else {
				timeoutDuration = remaining;
			}
		}
		sessionExpiryTimer = setTimeout(() => {
			stopRecordingSession();
		}, timeoutDuration);
	};

	const resetSessionExpiryTimer = () => {
		if (sessionExpiryTimer) {
			clearTimeout(sessionExpiryTimer);
		}
		startSessionExpiryTimer();
	};

	const stopSessionExpiryTimer = () => {
		if (sessionExpiryTimer) {
			clearTimeout(sessionExpiryTimer);
			sessionExpiryTimer = null;
		} else {
			log("No session expiry timer to stop");
		}
	};

	const ensureSessionContext = () => {
		if (!sessionId) {
			sessionId = config.sessionId || generateSessionId();
			beginNewSessionAttribution();
		}
		if (!userId) {
			userId = config.userId || getOrCreateUserId();
		}
		const previousSession = loadSessionFromStorage();
		if (!externalId && previousSession?.externalId) {
			externalId = previousSession.externalId || null;
		}
		organizationId = config.organizationId;
		if (!sessionStartTime) {
			sessionStartTime = now();
			resetCaptureDiagnosticsState();
		}
		isSampledIn = shouldSample(organizationId, sessionId);
	};

	const isSessionExpired = (
		sessionData: SessionData | SessionStub | null,
	): boolean => {
		if (!sessionData) return true;
		const withTimes = sessionData as Partial<SessionData>;
		if (
			withTimes.sessionId == null ||
			withTimes.lastActivity == null ||
			withTimes.lastActivity === undefined
		) {
			return true;
		}
		const timeSinceLastActivity = now() - withTimes.lastActivity;
		const totalSessionDuration =
			withTimes.sessionStartTime != null
				? now() - withTimes.sessionStartTime
				: 0;
		return (
			timeSinceLastActivity > config.sessionTimeout ||
			totalSessionDuration > config.sessionTimeout
		);
	};

	const fetchWithTimeout = async (
		url: string,
		options: RequestInit = {},
		timeoutMs = 20_000,
	) => {
		const controller = new AbortController();
		const timeoutId =
			timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
		const mergedOptions = { ...options, signal: controller.signal };
		try {
			return await fetch(url, mergedOptions);
		} catch (err) {
			if (
				err instanceof DOMException &&
				(err.name === "AbortError" || err.name === "TimeoutError")
			) {
				return { ok: false, status: 0, statusText: "Aborted" };
			}
			if (typeof err === "object") {
				const message = (err as { message?: string }).message || "";
				if (message.includes("aborted") || message.includes("cancelled")) {
					return { ok: false, status: 0, statusText: "Aborted" };
				}

				if (message.includes("limit exceeded")) {
					return { ok: false, status: 0, statusText: "Limit Exceeded" };
				}
			}
			return { ok: false, status: 0, statusText: "Aborted" };
		} finally {
			if (timeoutId) clearTimeout(timeoutId);
		}
	};

	const fetchWithRetry = async (
		url: string,
		options: RequestInit = {},
		timeoutMs = 20_000,
		retries = 2,
		backoffMs = 1000,
	) => {
		try {
			const response = await fetchWithTimeout(url, options, timeoutMs);
			if (
				response?.ok ||
				(response?.status > 0 && response?.status < 500) ||
				!retries
			) {
				return response;
			}
			error(`Failed to fetch ${url}:`, response);
		} catch (err) {
			error(`Failed to fetch ${url}:`, err);
			if (!retries) {
				return { ok: false, status: 0, statusText: "Aborted" };
			}
		}

		log(`Will retry ${url} in ${backoffMs}ms`);
		await new Promise((resolve) => setTimeout(resolve, backoffMs));
		return fetchWithRetry(url, options, timeoutMs, retries - 1, backoffMs * 2);
	};

	const buildEndpointUrl = (
		baseEndpoint: string,
		requestType: string,
		keepalive: boolean | null,
		payloadSizeKB: number,
		isCompressed = false,
		sessionIdParam: string | null = null,
	) => {
		let url: URL;
		try {
			url = new URL(baseEndpoint);
		} catch {
			return baseEndpoint;
		}
		const currentSessionId =
			sessionIdParam !== null ? sessionIdParam : sessionId;
		url.searchParams.set(URL_PARAMS.VERSION, LIB_VERSION);
		url.searchParams.set(URL_PARAMS.SDK_NAME, sdkName);
		url.searchParams.set(URL_PARAMS.REQUEST_TYPE, requestType);
		url.searchParams.set(
			URL_PARAMS.KEEPALIVE,
			keepalive === null ? "null" : String(keepalive),
		);
		url.searchParams.set(URL_PARAMS.SIZE, Math.round(payloadSizeKB).toString());
		url.searchParams.set(URL_PARAMS.COMPRESSION, isCompressed ? "1" : "0");
		if (currentSessionId) {
			url.searchParams.set(URL_PARAMS.SESSION_ID, currentSessionId);
		}
		return url.toString();
	};

	const buildDiagnosticsPayload = (
		firstIngest: { ok: boolean; status: number | null } | null,
		isFollowup: boolean,
	) => {
		const elapsedMs = sessionStartTime ? now() - sessionStartTime : 0;
		return {
			first_rrweb_event_type: firstRrwebEventType,
			full_snapshot_seen: fullSnapshotSeen,
			full_snapshot_filtered_reason: fullSnapshotFilteredReason,
			events_sent_count: eventsSentCount,
			ingest_attempts_count: ingestAttemptsCount,
			window_ms: Math.min(60_000, Math.max(0, elapsedMs)),
			first_ingest_attempt: firstIngest
				? {
						ok: firstIngest.ok,
						status: firstIngest.status,
					}
				: null,
			idle_ms_when_dropped: idleMsWhenSnapshotDropped,
			page_hidden_at_start: pageHiddenAtStart,
			is_session_restart: isSessionRestart,
			start_recording_error: startRecordingError,
			// Snapshot-pipeline instrumentation — populated step-by-step so we can
			// pinpoint exactly where a missing snapshot fell off the conveyor belt.
			snapshot_emit_count: snapshotEmitCount,
			snapshot_filter_result: snapshotFilterResult,
			snapshot_rebuild_error: snapshotRebuildError,
			snapshot_pack_ok: snapshotPackOk,
			snapshot_pack_error: snapshotPackError,
			snapshot_buffer_result: snapshotBufferResult,
			snapshot_buffer_timing_ms: snapshotBufferTimingMs,
			snapshot_ingest_status: snapshotIngestStatus,
			snapshot_ingest_ok: snapshotIngestOk,
			snapshot_transport: snapshotTransport,
			// True when the watchdog fired while the snapshot's first send was still
			// in flight (its ingest status is reported by the follow-up beacon below).
			snapshot_pending_at_watchdog: snapshotPendingAtWatchdog,
			// Whether a chunk send actually carried the snapshot (status resolved).
			snapshot_sent: snapshotIngestStatus != null,
			// false on the primary beacon, true on the snapshot-ingest follow-up.
			is_followup: isFollowup,
		};
	};

	const diagnosticsErrorLine = (
		label: string,
		payload: ReturnType<typeof buildDiagnosticsPayload>,
	) =>
		`${label}: full_snapshot_seen=${payload.full_snapshot_seen} reason=${payload.full_snapshot_filtered_reason} first_event_type=${payload.first_rrweb_event_type ?? "null"} events_sent=${payload.events_sent_count} ingest_attempts=${payload.ingest_attempts_count} window_ms=${payload.window_ms} idle_ms=${payload.idle_ms_when_dropped ?? "null"} page_hidden=${payload.page_hidden_at_start} restart=${payload.is_session_restart} start_error=${payload.start_recording_error ?? "null"} snap_emits=${payload.snapshot_emit_count} snap_filter=${payload.snapshot_filter_result ?? "null"} snap_rebuild_err=${payload.snapshot_rebuild_error ?? "null"} snap_pack_ok=${payload.snapshot_pack_ok ?? "null"} snap_pack_err=${payload.snapshot_pack_error ?? "null"} snap_buffer=${payload.snapshot_buffer_result ?? "null"} snap_buffer_ms=${payload.snapshot_buffer_timing_ms ?? "null"} snap_ingest_status=${payload.snapshot_ingest_status ?? "null"} snap_ingest_ok=${payload.snapshot_ingest_ok ?? "null"} snap_transport=${payload.snapshot_transport ?? "null"} snap_sent=${payload.snapshot_sent} snap_pending_at_watchdog=${payload.snapshot_pending_at_watchdog}`;

	const emitDiagnosticEvent = (
		payload: ReturnType<typeof buildDiagnosticsPayload>,
	) => {
		const diagnosticEvent: IwCustomEvent<typeof payload> = {
			type: 5,
			data: {
				tag: "iw-capture-diagnostics",
				payload,
			},
			timestamp: now(),
		};
		void addEvent(diagnosticEvent);
	};

	const emitCaptureDiagnostics = (
		firstIngest: { ok: boolean; status: number | null } | null = null,
	) => {
		if (captureDiagnosticsEmitted || !diagnosticArmed) return;
		const payload = buildDiagnosticsPayload(firstIngest, false);
		captureDiagnosticsEmitted = true;
		clearCaptureDiagnosticsWatchdog();
		emitDiagnosticEvent(payload);
		// We also want to surface the snapshot pipeline state for the case where the SDK
		// THINKS the snapshot was captured (full_snapshot_seen=true) but the replay is
		// still blank — that's the bug we're hunting now. Fire the error beacon when
		// either the snapshot isn't seen OR the lifecycle shows it dropped mid-flight.
		const snapshotLostDespiteSeen =
			fullSnapshotSeen &&
			(snapshotBufferResult !== "pushed" || snapshotIngestOk === false);
		if (!fullSnapshotSeen || snapshotLostDespiteSeen) {
			error(diagnosticsErrorLine("iw-capture-diagnostics", payload));
		}
	};

	// Fired only when the watchdog already latched the primary beacon while the
	// snapshot's first send was still pending (snapshotPendingAtWatchdog). Reports
	// the snapshot's real ingest outcome once it resolves (or never, via the
	// fallback timer). At most one per session; independent latch from the primary.
	const emitSnapshotFollowup = () => {
		if (
			snapshotFollowupEmitted ||
			!snapshotPendingAtWatchdog ||
			!diagnosticArmed
		)
			return;
		snapshotFollowupEmitted = true;
		clearCaptureDiagnosticsFollowup();
		const payload = buildDiagnosticsPayload(null, true);
		emitDiagnosticEvent(payload);
		// Only beacon an error when the follow-up confirms a real failure or a
		// snapshot that was never sent. A late-but-successful send (200/202) still
		// emits the type-5 event (cheap, joins the record) but must not add noise.
		if (snapshotIngestOk === false || snapshotIngestStatus == null) {
			error(diagnosticsErrorLine("iw-capture-diagnostics(followup)", payload));
		}
	};

	const sendEvents = async (events: unknown[], isUnloading = false) => {
		if (!events || events.length === 0) {
			log("No events to send, skipping network request");
			return;
		}
		if (contractStatus) {
			log("Events not sent - Contract issue:", contractStatus.message);
			return;
		}
		if (quotaStatus) {
			log("Events not sent - Quota exceeded:", quotaStatus.message);
			return;
		}
		if (config.defaultSamplingPercent === 0) {
			log("Events not sent - samplingPercent set to 0");
			return;
		}
		if (!sessionId || !userId) {
			error("Cannot send events: sessionId or userId not set");
			return;
		}
		const capturedSessionId = sessionId;
		const capturedUserId = userId;
		const capturedExternalId = externalId;
		const capturedOrganizationId = organizationId;
		if (sessionStartTime && now() - sessionStartTime <= 60_000) {
			eventsSentCount += events.length;
		}
		const noteFirstIngestAttempt = (
			ok: boolean,
			status: number | null,
			transport: "keepalive" | "beacon" | "fetch",
		) => {
			// Snapshot ingest tracking: if the snapshot was buffered before this send,
			// this chunk's outcome is the snapshot's outcome (snapshots flush eagerly,
			// so they ride out in the next send). Captures the actual server response,
			// not just whether the SDK attempted the send.
			if (snapshotPendingIngest && snapshotIngestStatus == null) {
				snapshotIngestStatus = status;
				snapshotIngestOk = ok;
				// A blocked transport is a failed send that never got an HTTP response
				// (status 0/null). A real HTTP status like 403 is NOT blocked — it
				// reached an HTTP server, which is the distinction we care about.
				const blocked = !ok && (status === 0 || status == null);
				snapshotTransport = blocked ? `${transport}_blocked` : transport;
				snapshotPendingIngest = false;
				// If the watchdog already fired the primary beacon while this send was
				// pending, report the now-resolved snapshot status via the follow-up.
				if (snapshotPendingAtWatchdog && !snapshotFollowupEmitted) {
					emitSnapshotFollowup();
				}
			}
			if (firstIngestAttemptLogged) return;
			firstIngestAttemptLogged = true;
			emitCaptureDiagnostics({ ok, status });
		};

		const eventsToSend = [...events];
		const MAX_CHUNK_SIZE = 40 * BYTES_IN_KILOBYTE;

		const basePayload = {
			session_id: capturedSessionId,
			user_id: capturedUserId,
			organization_id: capturedOrganizationId,
			events: [] as unknown[],
			client_version: LIB_VERSION,
			client_sdk: sdkName,
		};
		if (capturedExternalId) {
			(basePayload as { external_id?: string }).external_id =
				capturedExternalId;
		}
		const basePayloadString = JSON.stringify(basePayload);
		const basePayloadSize = new Blob([basePayloadString]).size;
		const fullPayloadString = JSON.stringify({
			...basePayload,
			events: eventsToSend,
		});
		const fullPayloadSize = new Blob([fullPayloadString]).size;

		let chunks: unknown[][] = [];
		if (fullPayloadSize > MAX_CHUNK_SIZE) {
			let currentChunk: unknown[] = [];
			let currentChunkSize = basePayloadSize;
			for (let i = 0; i < eventsToSend.length; i += 1) {
				const testChunk = [...currentChunk, eventsToSend[i]];
				const testPayloadSize = new Blob([
					JSON.stringify({ ...basePayload, events: testChunk }),
				]).size;
				if (testPayloadSize > MAX_CHUNK_SIZE && currentChunk.length > 0) {
					chunks.push([...currentChunk]);
					currentChunk = [eventsToSend[i]];
					currentChunkSize = new Blob([
						JSON.stringify({ ...basePayload, events: currentChunk }),
					]).size;
				} else {
					currentChunk = testChunk;
					currentChunkSize = testPayloadSize;
				}
				void currentChunkSize;
			}
			if (currentChunk.length > 0) {
				chunks.push(currentChunk);
			}
		} else {
			chunks = [eventsToSend];
		}

		const sendChunk = async (chunkIndex: number, chunk: unknown) => {
			if (sessionStartTime && now() - sessionStartTime <= 60_000) {
				ingestAttemptsCount += 1;
			}
			const chunkPayload = {
				session_id: capturedSessionId,
				user_id: capturedUserId,
				organization_id: capturedOrganizationId,
				events: chunk,
				client_version: LIB_VERSION,
				client_sdk: sdkName,
			};
			if (capturedExternalId) {
				(chunkPayload as { external_id?: string }).external_id =
					capturedExternalId;
			}
			const chunkPayloadString = JSON.stringify(chunkPayload);
			// In debug mode: send plain string (readable + easy to inspect).
			const compressionResult = config.debug
				? {
						body: chunkPayloadString,
						compressed: false,
						originalSize: new Blob([chunkPayloadString]).size,
					}
				: await compressGzip(chunkPayloadString);

			const bodyToSend = compressionResult.body;
			const bodySizeBytes =
				typeof bodyToSend === "string"
					? new Blob([bodyToSend]).size
					: bodyToSend instanceof Blob
						? bodyToSend.size
						: new Blob([bodyToSend as unknown as ArrayBuffer]).size;
			const payloadSizeKB = bodySizeBytes / BYTES_IN_KILOBYTE;

			// Use fetch+keepalive if request is small enough
			if (bodySizeBytes <= KEEPALIVE_MAX_BYTES) {
				const endpointUrl = buildEndpointUrl(
					config.endpoint,
					"f",
					true,
					payloadSizeKB,
					compressionResult.compressed,
					capturedSessionId,
				);

				// If unloading disable timeout and retries as they won't be able to do anything anyway
				const response = await fetchWithRetry(
					endpointUrl,
					{
						method: "POST",
						body: bodyToSend,
						keepalive: true,
					},
					isUnloading ? 0 : 20_000,
					isUnloading ? 0 : 2,
					config.sendBackoffMs,
				);

				if (
					response?.ok ||
					// Don't retry client errors
					(response?.status > 0 && response?.status < 500)
				) {
					noteFirstIngestAttempt(
						Boolean(response?.ok),
						response?.status ?? null,
						"keepalive",
					);
					transportBlocked = false;
					return;
				}
				noteFirstIngestAttempt(
					Boolean(response?.ok),
					response?.status ?? null,
					"keepalive",
				);
			}

			// Try sendBeacon as last resort during unload
			if (isUnloading && isBrowser() && navigator.sendBeacon) {
				try {
					const endpointUrl = buildEndpointUrl(
						config.endpoint,
						"b",
						null,
						payloadSizeKB,
						compressionResult.compressed,
						capturedSessionId,
					);

					const sent = navigator.sendBeacon(endpointUrl, bodyToSend);
					if (sent) {
						noteFirstIngestAttempt(true, 0, "beacon");
						transportBlocked = false;
						return;
					}
				} catch (err) {
					error(`sendBeacon for chunk ${chunkIndex + 1} failed:`, err);
					noteFirstIngestAttempt(false, null, "beacon");
				}
			} else {
				log(`sendBeacon not supported for chunk ${chunkIndex + 1}`);
			}

			// Otherwise try normal fetch
			const endpointUrl = buildEndpointUrl(
				config.endpoint,
				"f",
				false,
				payloadSizeKB,
				compressionResult.compressed,
				capturedSessionId,
			);
			// If unloading disable timeout and retries as they won't be able to do anything anyway
			const response = await fetchWithRetry(
				endpointUrl,
				{
					method: "POST",
					body: bodyToSend,
				},
				isUnloading ? 0 : 20_000,
				isUnloading ? 0 : 3,
				config.sendBackoffMs,
			);

			if (!response?.ok) {
				if (response?.status === 0) {
					transportBlocked = true;
				}
				error(`Failed to send chunk ${chunkIndex + 1}:`, response.status);
				noteFirstIngestAttempt(false, response?.status ?? null, "fetch");
			} else {
				transportBlocked = false;
				noteFirstIngestAttempt(true, response?.status ?? null, "fetch");
			}
		};

		// If unloading just trigger all the sends and let the browser manage the queue hoping to send as many as possible
		if (isUnloading) {
			chunks.map((chunk, chunkIndex) => sendChunk(chunkIndex, chunk));
			return;
		}

		// Otherwise go sequentially to try and fit within keepalive in-flight limits
		for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
			const chunk = chunks[chunkIndex];
			try {
				await sendChunk(chunkIndex, chunk);
			} catch (err) {
				const errorMessage = (err as { message?: string })?.message || "";
				const isBlocked =
					errorMessage.includes("Failed to fetch") ||
					errorMessage.includes("NetworkError") ||
					errorMessage.includes("ERR_BLOCKED_BY_CLIENT") ||
					errorMessage.includes("ERR_BLOCKED_BY_RESPONSE") ||
					errorMessage.includes("net::ERR_FAILED");
				const isAborted =
					(err as { name?: string })?.name === "AbortError" ||
					(err as { name?: string })?.name === "TimeoutError" ||
					errorMessage.includes("aborted") ||
					errorMessage.includes("cancelled");
				if (isBlocked || isAborted) {
					transportBlocked = true;
					return;
				}
				return;
			}
		}
	};

	const flushEvents = (isUnloading = false) => {
		if (eventBuffer.length === 0) {
			log("No events in buffer to flush");
			return;
		}
		if (sessionStartTime !== null && config.minSessionDuration > 0) {
			const elapsed = now() - sessionStartTime;
			if (elapsed < config.minSessionDuration) {
				if (isUnloading) {
					eventBuffer = [];
				}
				return;
			}
		}
		const eventsToSend = [...eventBuffer];
		eventBuffer = [];
		void sendEvents(eventsToSend, isUnloading);
	};

	const startFlushTimer = () => {
		if (flushTimer) {
			log("Clearing existing flush timer");
			clearInterval(flushTimer);
		}
		flushTimer = setInterval(() => {
			flushEvents();
		}, config.flushInterval);
	};

	const stopFlushTimer = () => {
		if (flushTimer) {
			clearInterval(flushTimer);
			flushTimer = null;
		} else {
			log("No flush timer to stop");
		}
	};

	const loadRrweb = async (): Promise<NonNullable<Window["rrweb"]>> => {
		if (!isBrowser()) {
			throw new Error("rrweb requires a browser environment");
		}
		if (window.rrweb?.record) {
			return window.rrweb;
		}
		return {
			record: bundledRecord as NonNullable<Window["rrweb"]>["record"],
		};
	};

	const loadRrwebPacker = async (): Promise<NonNullable<Window["pack"]>> => {
		if (!isBrowser()) {
			throw new Error("rrweb packer requires a browser environment");
		}
		const existing =
			window.rrwebPack?.pack || window.rrwebRecord?.pack || window.pack;
		if (typeof existing === "function") {
			return existing;
		}
		return bundledPack as NonNullable<Window["pack"]>;
	};

	const addEvent = async (event: unknown): Promise<SnapshotBufferResult> => {
		if (hardStop) return "dropped_hardstop";
		if (urlFilterPaused) return "dropped_url_filter";

		// Check if current session has expired
		if (isSessionActive && sessionStartTime) {
			const totalSessionDuration = now() - sessionStartTime;
			if (totalSessionDuration > config.sessionTimeout) {
				log("Session expired, stopping and restarting with new session");
				stopRecordingSession();
				// Fall through to the !isSessionActive block below to create new session
			}
		}

		if (!isSessionActive) {
			if (stopRecording) {
				stopRecording();
				stopRecording = null;
			}
			sessionId = config.sessionId || generateSessionId();
			userId = config.userId || getOrCreateUserId();
			const previousSession = loadSessionFromStorage();
			if (previousSession?.externalId) {
				externalId = previousSession.externalId || null;
			}
			organizationId = config.organizationId;
			sessionStartTime = now();
			beginNewSessionAttribution();
			resetCaptureDiagnosticsState();
			isSessionRestart = true; // flag set after reset so it survives into the new session's diagnostic
			isSampledIn = shouldSample(organizationId, sessionId);
			await startRecording();
			return "dropped_restart";
		}
		eventBuffer.push(event);
		updateSessionActivity();
		if (eventBuffer.length >= config.batchSize) {
			log("Buffer size reached, triggering flush");
			if (snapshotFlushTimeout) {
				clearTimeout(snapshotFlushTimeout);
				snapshotFlushTimeout = null;
			}
			flushEvents();
		} else {
			const isFullSnapshot = isRecord(event) && event.type === 2;
			if (isFullSnapshot) {
				if (snapshotFlushTimeout) clearTimeout(snapshotFlushTimeout);
				snapshotFlushTimeout = setTimeout(() => {
					snapshotFlushTimeout = null;
					flushEvents();
				}, 200);
			} else {
				log("Buffer not full yet, continuing to collect events");
			}
		}
		return "pushed";
	};

	const generateInsights = async (hardSessionId: string | null = null) => {
		if (!config.generateInsights) {
			return;
		}

		sessionId = hardSessionId || sessionId;
		if (!sessionId || !organizationId) {
			return;
		}

		// Backend base URL (no ingest); append sessions insights path
		const base = (config.endpointInsights || config.baseUrl || "")
			.toString()
			.replace(/\/$/, "");
		if (!base) return;
		const endpointUrl = `${base}/v1/sessions/${encodeURIComponent(sessionId)}/insights`;

		const payload = { organization_id: organizationId };
		const payloadString = JSON.stringify(payload);

		try {
			const response = await fetch(endpointUrl, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: payloadString,
				mode: "cors",
				credentials: "omit",
			});
			if (response.status === 0) {
				log("Insights request aborted (status 0)");
			} else if (!response.ok && response.status >= 500) {
				error("Failed to send insights with fetch:", response.status);
			}
		} catch (err) {
			const errorMessage = (err as { message?: string })?.message || "";
			const isBlocked =
				errorMessage.includes("Failed to fetch") ||
				errorMessage.includes("NetworkError") ||
				errorMessage.includes("ERR_BLOCKED_BY_CLIENT") ||
				errorMessage.includes("ERR_BLOCKED_BY_RESPONSE") ||
				errorMessage.includes("net::ERR_FAILED");
			const isAborted =
				(err as { name?: string })?.name === "AbortError" ||
				(err as { name?: string })?.name === "TimeoutError" ||
				errorMessage.includes("aborted") ||
				errorMessage.includes("cancelled");
			if (isBlocked || isAborted) {
				return;
			}
			return;
		}
	};

	const waitForStylesheets = () =>
		new Promise<void>((resolve) => {
			if (document.readyState === "complete") {
				resolve();
				return;
			}
			const timer = setTimeout(() => {
				window.removeEventListener("load", onLoad);
				log("window.load fallback fired after 3s");
				resolve();
			}, 3000);
			const onLoad = () => {
				clearTimeout(timer);
				resolve();
			};
			window.addEventListener("load", onLoad, { once: true });
		});

	const captureAdoptedStyleSheets = () => {
		try {
			for (const el of document.querySelectorAll("style[data-iw-adopted]")) {
				el.remove();
			}

			const sheets = document.adoptedStyleSheets;
			if (!sheets || sheets.length === 0) return;
			for (const sheet of sheets) {
				try {
					const rules = Array.from(sheet.cssRules);
					if (rules.length === 0) continue;
					const style = document.createElement("style");
					style.setAttribute("data-iw-adopted", "true");
					style.textContent = rules.map((r) => r.cssText).join("\n");
					document.head.appendChild(style);
				} catch {
					log("Skipping adopted stylesheet (CORS/SecurityError)");
				}
			}
		} catch {
			log("adoptedStyleSheets API not supported");
		}
	};

	const installDynamicStylesheetObserver = () => {
		if (dynamicStyleObserver) {
			dynamicStyleObserver.disconnect();
		}
		dynamicStyleObserver = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				for (const node of mutation.addedNodes) {
					if (
						node instanceof HTMLLinkElement &&
						node.rel === "stylesheet" &&
						node.href
					) {
						const href = node.href;
						fetch(href)
							.then((res) => res.text())
							.then((css) => {
								const style = document.createElement("style");
								style.setAttribute("data-iw-dynamic", "true");
								style.textContent = css;
								if (node.parentNode) {
									node.replaceWith(style);
								} else {
									document.head.appendChild(style);
								}
							})
							.catch(() => {
								log("Could not inline dynamic stylesheet:", href);
							});
					}
				}
			}
		});
		dynamicStyleObserver.observe(document.head, {
			childList: true,
			subtree: true,
		});
		dynamicStyleObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});
	};

	const startRecording = async () => {
		if (isSessionActive) {
			log("Recording already active, skipping startRecording");
			return;
		}
		isSessionActive = true;
		pageHiddenAtStart = getDocument()?.hidden ?? null;
		diagnosticArmed = true;
		clearCaptureDiagnosticsWatchdog();
		captureDiagnosticsWatchdog = setTimeout(() => {
			captureDiagnosticsWatchdog = null;
			if (captureDiagnosticsEmitted || !diagnosticArmed) return;
			if (!fullSnapshotSeen && fullSnapshotFilteredReason === "none") {
				fullSnapshotFilteredReason =
					firstRrwebEventType == null
						? "rrweb_never_emitted"
						: "rrweb_emit_no_snapshot";
			}
			// Blind-spot guard: the snapshot was buffered but its first send hasn't
			// resolved yet, so the primary beacon would report snapshot_ingest_status
			// =null. Arm a follow-up that reports the snapshot's real outcome once the
			// send resolves (noteFirstIngestAttempt), with a fallback timer for the
			// case where it is never sent at all.
			if (snapshotPendingIngest && snapshotIngestStatus == null) {
				snapshotPendingAtWatchdog = true;
				clearCaptureDiagnosticsFollowup();
				snapshotFollowupTimer = setTimeout(
					emitSnapshotFollowup,
					CAPTURE_DIAGNOSTICS_FOLLOWUP_MS,
				);
			}
			emitCaptureDiagnostics(null);
		}, CAPTURE_DIAGNOSTICS_WATCHDOG_MS);
		try {
			const [packFn, rrweb] = await Promise.all([
				loadRrwebPacker(),
				loadRrweb(),
			]);

			if (config.inlineStylesheet) {
				await waitForStylesheets();
				captureAdoptedStyleSheets();
				installDynamicStylesheetObserver();
			}

			const filterBlacklistedAttrMutations = (event: eventWithTime | null) => {
				const data = getMutationData(event);
				if (!data) return event;
				if (!Array.isArray(data.attributes)) {
					return event;
				}
				const prefixes = Array.isArray(config.blackList)
					? config.blackList.filter(
							(p) => typeof p === "string" && p.length > 0,
						)
					: [];
				if (prefixes.length === 0) {
					return event;
				}
				const startsWithBlacklisted = (idValue: unknown) => {
					if (typeof idValue !== "string") return false;
					for (const prefix of prefixes) {
						if (idValue.startsWith(prefix)) return true;
					}
					return false;
				};
				const nextAttributes = data.attributes.filter((attrChange) => {
					const elemId = (attrChange as attributeMutation | null)?.attributes
						?.id;
					return !startsWithBlacklisted(elemId);
				});
				// event is guaranteed non-null here since data was obtained from getMutationData(event)
				const baseEvent = event as eventWithTime;
				const nextData = { ...data, attributes: nextAttributes };
				const hasAdds =
					Array.isArray(nextData.adds) && nextData.adds.length > 0;
				const hasRemoves =
					Array.isArray(nextData.removes) && nextData.removes.length > 0;
				const hasTexts =
					Array.isArray(nextData.texts) && nextData.texts.length > 0;
				const hasAttributes =
					Array.isArray(nextData.attributes) && nextData.attributes.length > 0;
				if (!hasAdds && !hasRemoves && !hasTexts && !hasAttributes) {
					return null;
				}
				return {
					...baseEvent,
					data: nextData,
				} as eventWithTime;
			};

			const filterMutations = (event: eventWithTime | null) => {
				const data = getMutationData(event);
				if (data) {
					const attrs = data.attributes;
					if (Array.isArray(attrs) && attrs.length) {
						const filtered = attrs.filter((a) => {
							const at = isRecord((a as attributeMutation | null)?.attributes)
								? ((a as attributeMutation).attributes as Record<
										string,
										unknown
									>)
								: {};
							const keys = Object.keys(at);
							const onlyTransformOpacity =
								keys.length > 0 &&
								keys.every((k) => k === "transform" || k === "opacity");
							if (!onlyTransformOpacity) return true;
							const id = at.id;
							if (isInteractiveId(id)) return true;
							return false;
						});
						if (filtered.length === 0) return null;
						data.attributes = filtered;
					}
				}
				return event;
			};

			const isNoisySvgAdd = (add: addedNodeMutation) => {
				const n = add?.node as RrwebSerializedNodeWithMeta | undefined;
				if (!n) return false;
				if (!n.isSVG) return false;
				const tag = (n.tagName || "").toLowerCase();
				if (
					tag === "path" &&
					typeof (isRecord(n.attributes) ? n.attributes.d : undefined) ===
						"string" &&
					(isRecord(n.attributes) ? String(n.attributes.d).length : 0) > 200
				)
					return true;
				if (tag === "style") return true;
				return false;
			};

			const isUselessTextAdd = (add: addedNodeMutation) => {
				const n = add?.node as RrwebSerializedNodeWithMeta | undefined;
				return n?.type === 3 && (n.textContent === "" || n.textContent == null);
			};

			const filterAdds = (event: eventWithTime | null) => {
				const data = getMutationData(event);
				if (!data) return event;
				if (Array.isArray(data.adds) && data.adds.length) {
					data.adds = data.adds.filter(
						(add) => !isNoisySvgAdd(add) && !isUselessTextAdd(add),
					);
				}
				const hasAdds = Array.isArray(data.adds) && data.adds.length > 0;
				const hasRemoves =
					Array.isArray(data.removes) && data.removes.length > 0;
				const hasTexts = Array.isArray(data.texts) && data.texts.length > 0;
				const hasAttributes =
					Array.isArray(data.attributes) && data.attributes.length > 0;
				if (!hasAdds && !hasRemoves && !hasTexts && !hasAttributes) {
					return null;
				}
				return event;
			};

			const NET_SAMPLE_MS = 10_000;
			const IDLE_MS = 15_000;
			const BLOCKED_NET_HOSTS = new Set([
				"api.infinitewatch.ai",
				"ingest.infinitewatch.ai",
				"ingest.humanbehavior.co",
			]);
			let __iw_lastUserIntentAt = now();
			let __iw_intentInstalled = false;
			const __iw_lastNetSentAt = new Map<string, number>();

			const iwInstallUserIntentListenersOnce = () => {
				if (__iw_intentInstalled || !isBrowser()) return;
				__iw_intentInstalled = true;
				const mark = () => {
					__iw_lastUserIntentAt = now();
				};
				[
					"pointerdown",
					"mousedown",
					"touchstart",
					"keydown",
					"wheel",
					"scroll",
					"input",
				].forEach((t) => {
					window.addEventListener(t, mark, { capture: true, passive: true });
				});
			};

			const iwKeyForRequest = (name: string) => {
				try {
					const u = new URL(name);
					return u.hostname + u.pathname;
				} catch {
					return String(name || "");
				}
			};

			const iwIsBlockedHost = (name: string) => {
				try {
					const host = new URL(name).hostname;
					return BLOCKED_NET_HOSTS.has(host);
				} catch {
					return false;
				}
			};

			const filterNetworkNoise = (event: eventWithTime | null) => {
				if (
					!event ||
					event.type !== 6 ||
					!(event.data as { plugin?: string } | undefined)?.plugin ||
					(event.data as { plugin?: string }).plugin !== "rrweb/network@1"
				) {
					return event;
				}
				if (now() - __iw_lastUserIntentAt > IDLE_MS) {
					return null;
				}
				const payload = (event.data as { payload?: { requests?: unknown } })
					?.payload;
				const reqs = payload?.requests;
				if (!Array.isArray(reqs) || reqs.length === 0) return event;
				const timestamp = event.timestamp || now();
				const filteredReqs: Record<string, unknown>[] = [];
				for (const r of reqs) {
					const name = (r as Record<string, unknown> | null)?.name;
					if (typeof name !== "string") continue;
					if (iwIsBlockedHost(name)) continue;
					const key = iwKeyForRequest(name);
					const last = __iw_lastNetSentAt.get(key) || 0;
					if (timestamp - last < NET_SAMPLE_MS) continue;
					__iw_lastNetSentAt.set(key, timestamp);
					filteredReqs.push(r as Record<string, unknown>);
				}
				if (filteredReqs.length === 0) return null;
				return {
					...event,
					data: {
						...event.data,
						payload: {
							...payload,
							requests: filteredReqs,
						},
					},
				};
			};

			const ANIM_ATTR_KEYS = new Set(["d"]);
			const ANIM_STYLE_KEYS = new Set([
				"transform",
				"opacity",
				"filter",
				"backdrop-filter",
				"will-change",
				"translate",
				"scale",
				"rotate",
			]);

			const isAnimationOnlyAttrChange = (attrChange: attributeMutation) => {
				const attrs = (attrChange as attributeMutation | null)?.attributes;
				if (!attrs || typeof attrs !== "object") return false;
				const keys = Object.keys(attrs);
				const firstKey = keys[0];
				if (keys.length === 1 && firstKey && ANIM_ATTR_KEYS.has(firstKey))
					return true;
				if (keys.length === 1 && firstKey === "style") {
					const style = (attrs as Record<string, unknown>).style;
					if (!style || typeof style !== "object") return false;
					const styleKeys = Object.keys(style);
					if (styleKeys.length === 0) return false;
					return styleKeys.every((k) => ANIM_STYLE_KEYS.has(k));
				}
				return keys.every((k) => ANIM_STYLE_KEYS.has(k));
			};

			const isPureAnimationMutationEvent = (event: eventWithTime | null) => {
				const data = getMutationData(event);
				if (!data) return false;
				const hasAdds = Array.isArray(data.adds) && data.adds.length > 0;
				const hasRemoves =
					Array.isArray(data.removes) && data.removes.length > 0;
				const hasTexts = Array.isArray(data.texts) && data.texts.length > 0;
				if (hasAdds || hasRemoves || hasTexts) return false;
				const attrs = data.attributes;
				if (!Array.isArray(attrs) || attrs.length === 0) return false;
				return attrs.every(isAnimationOnlyAttrChange);
			};

			const IDLE_MUTATION_SAMPLE_MS = 1_500;
			let __iw_lastKeptIdleAnimMutationAt = 0;

			const sampleAnimationMutationsWhenIdle = (
				event: eventWithTime | null,
			) => {
				if (!event || event.type !== 3 || event?.data?.source !== 0)
					return event;
				const idleFor = now() - __iw_lastUserIntentAt;
				if (idleFor <= IDLE_MS) return event;
				if (!isPureAnimationMutationEvent(event)) return event;
				const ts = event.timestamp || now();
				if (ts - __iw_lastKeptIdleAnimMutationAt < IDLE_MUTATION_SAMPLE_MS) {
					return null;
				}
				__iw_lastKeptIdleAnimMutationAt = ts;
				return event;
			};

			const dropIdleAnimationMutations = (event: eventWithTime | null) => {
				const data = getMutationData(event);
				if (!data) return event;
				const idleFor = now() - __iw_lastUserIntentAt;
				if (idleFor > IDLE_MS && isPureAnimationMutationEvent(event))
					return null;
				return event;
			};

			const DROP_ATTR_KEYS = new Set(["decoding", "data-hydrated"]);
			const filterLowValueAttributes = (event: eventWithTime | null) => {
				const data = getMutationData(event);
				if (!data) return event;
				const attrs = data.attributes;
				if (!Array.isArray(attrs) || attrs.length === 0) return event;
				const next = [];
				for (const a of attrs) {
					const at = (a as attributeMutation | null)?.attributes;
					if (!at || typeof at !== "object") {
						next.push(a);
						continue;
					}
					const newAttrs = { ...at };
					for (const k of Object.keys(newAttrs)) {
						if (DROP_ATTR_KEYS.has(k)) delete newAttrs[k];
					}
					if (
						"preserveAspectRatio" in newAttrs &&
						String(newAttrs.preserveAspectRatio).toLowerCase() === "none"
					) {
						delete newAttrs.preserveAspectRatio;
					}
					if ("width" in newAttrs && String(newAttrs.width) === "100%")
						delete newAttrs.width;
					if ("height" in newAttrs && String(newAttrs.height) === "100%")
						delete newAttrs.height;
					if (Object.keys(newAttrs).length === 0) continue;
					next.push({ ...a, attributes: newAttrs });
				}
				data.attributes = next;
				const empty =
					(!data.adds || data.adds.length === 0) &&
					(!data.attributes || data.attributes.length === 0) &&
					(!data.texts || data.texts.length === 0) &&
					(!data.removes || data.removes.length === 0);
				return empty ? null : event;
			};

			const filterIdleEvents = (event: eventWithTime | null) => {
				// Never drop the session's bootstrap full snapshot — without it the
				// whole session is unreplayable. Background tabs (browser throttling
				// delays the snapshot past IDLE_MS while the user never interacts)
				// and session restarts (which happen *because* of inactivity) emit
				// the first type-2 well inside the idle window; production beacons
				// showed idle_ms of 16s–6min at snapshot time. The filter's job is
				// dropping REDUNDANT mid-session re-snapshots, so only exempt while
				// no snapshot has been seen yet.
				if (event?.type === 2 && !fullSnapshotSeen) {
					return event;
				}
				if (now() - __iw_lastUserIntentAt > IDLE_MS) {
					if (event?.type === 2) {
						fullSnapshotFilteredReason = "idle_filter";
						idleMsWhenSnapshotDropped = now() - __iw_lastUserIntentAt;
					}
					if (event?.type === 3 && event?.data?.source === 0) {
						const hasAdds =
							Array.isArray(event.data.adds) && event.data.adds.length > 0;
						const hasRemoves =
							Array.isArray(event.data.removes) &&
							event.data.removes.length > 0;
						const hasTexts =
							Array.isArray(event.data.texts) && event.data.texts.length > 0;
						if (hasAdds || hasRemoves || hasTexts) {
							return event;
						}
					}
					return null;
				}
				return event;
			};

			const filterEvent = (event: eventWithTime | null) => {
				let filtered = event;
				// Pass through custom events (type 5) without DOM filters
				if (filtered?.type === 5) return filtered;
				filtered = filterIdleEvents(filtered);
				if (!filtered) return null;
				filtered = filterBlacklistedAttrMutations(filtered);
				if (!filtered) return null;
				filtered = filterMutations(filtered);
				if (!filtered) return null;
				filtered = filterAdds(filtered);
				if (!filtered) return null;
				filtered = filterNetworkNoise(filtered);
				if (!filtered) return null;
				filtered = sampleAnimationMutationsWhenIdle(filtered);
				if (!filtered) return null;
				filtered = dropIdleAnimationMutations(filtered);
				if (!filtered) return null;
				filtered = filterLowValueAttributes(filtered);
				if (!filtered) return null;
				return filtered;
			};

			const INTERACTIVE_SELECTOR =
				'a,button,input,select,textarea,label,[role="button"],[role="link"],[tabindex],[contenteditable="true"]';

			const isInteractiveEl = (el: Element | null) => {
				if (!el || el.nodeType !== 1) return false;
				return !!(
					el.matches?.(INTERACTIVE_SELECTOR) ||
					el.closest?.(INTERACTIVE_SELECTOR)
				);
			};

			const onlyAnimationStyleChanged = (oldStyle = "", newStyle = "") => {
				const toMap = (s: string) =>
					s
						.split(";")
						.map((x) => x.trim())
						.filter(Boolean)
						.map((x) => x.split(":").map((y) => y.trim()))
						.reduce<Record<string, string>>((acc, [k, v]) => {
							if (k) acc[k] = v ?? "";
							return acc;
						}, {});
				const oldMap = toMap(oldStyle);
				const newMap = toMap(newStyle);
				const changedKeys = new Set<string>();
				for (const k of new Set([
					...Object.keys(oldMap),
					...Object.keys(newMap),
				])) {
					if ((oldMap[k] ?? "") !== (newMap[k] ?? "")) changedKeys.add(k);
				}
				if (changedKeys.size === 0) return false;
				const ANIM_STYLE_PROPS = [
					"transform",
					"opacity",
					"will-change",
					"filter",
					"backdrop-filter",
					"translate",
					"scale",
					"rotate",
				];
				return [...changedKeys].every((k) =>
					ANIM_STYLE_PROPS.some((p) => k.includes(p)),
				);
			};

			const INTERACTIVE_TAGS = new Set([
				"a",
				"button",
				"input",
				"select",
				"textarea",
				"label",
			]);
			const INTERACTIVE_ROLES = new Set(["button", "link"]);
			let __iw_idIsInteractive = new Map<string, boolean>();

			const rebuildInteractiveIdMapFromFullSnapshot = (
				fsEvent: eventWithTime,
			) => {
				__iw_idIsInteractive = new Map();
				const root = (fsEvent?.data as { node?: RrwebSerializedNodeWithMeta })
					?.node;
				if (!root) return;
				const walk = (
					node: RrwebSerializedNodeWithMeta,
					interactiveAncestor = false,
				): void => {
					if (!node || typeof node !== "object") return;
					if (node.type !== 2) {
						if (node.childNodes) {
							node.childNodes.forEach((ch) => {
								walk(ch, interactiveAncestor);
							});
						}
						return;
					}
					const tag = String(node.tagName || "").toLowerCase();
					const attrs = node.attributes || {};
					const id = attrs.id;
					const selfInteractive =
						INTERACTIVE_TAGS.has(tag) ||
						(typeof attrs.role === "string" &&
							INTERACTIVE_ROLES.has(String(attrs.role).toLowerCase())) ||
						attrs.tabindex != null ||
						attrs.href != null ||
						String(attrs.contenteditable || "").toLowerCase() === "true";
					const isInteractive = interactiveAncestor || selfInteractive;
					if (typeof id === "string") {
						__iw_idIsInteractive.set(id, isInteractive);
					}
					if (node.childNodes) {
						node.childNodes.forEach((ch) => {
							walk(ch, isInteractive);
						});
					}
				};
				walk(root, false);
			};

			const isInteractiveId = (id: unknown) => {
				if (typeof id !== "string") return false;
				return __iw_idIsInteractive.get(id) === true;
			};

			type DocSizeReason = "start" | "route" | "observer" | "poll";

			// Apps disagree on whether <html> or <body> is the scroll container —
			// taking the max of both (plus clientWidth/clientHeight) is robust.
			const computeDocSize = (): { width: number; height: number } => {
				const doc = getDocument();
				if (!doc) return { width: 0, height: 0 };
				const docEl = doc.documentElement;
				const body = doc.body;
				const width = Math.max(
					docEl?.scrollWidth ?? 0,
					body?.scrollWidth ?? 0,
					docEl?.clientWidth ?? 0,
				);
				const height = Math.max(
					docEl?.scrollHeight ?? 0,
					body?.scrollHeight ?? 0,
					docEl?.clientHeight ?? 0,
				);
				return { width, height };
			};

			// Asymmetric Δ thresholds: height drives the backend's scroll-depth
			// ratio (tight 10 px gate), width is loose (50 px) to keep responsive
			// 1-px reflow noise from inflating event volume.
			const DOC_SIZE_DEDUP_HEIGHT_PX = 10;
			const DOC_SIZE_DEDUP_WIDTH_PX = 50;
			const DOC_SIZE_POLL_INTERVAL_MS = 5_000;
			const DOC_SIZE_DEBOUNCE_MS = 250;

			const emitDocSize = (reason: DocSizeReason) => {
				const win = getWindow();
				if (!win) return;
				const { width, height } = computeDocSize();
				// Page genuinely not laid out yet — skip entirely. If only one
				// dimension is zero, still emit; the non-zero side is useful.
				if (width <= 0 && height <= 0) return;

				if (
					(reason === "observer" || reason === "poll") &&
					lastEmittedDocSize
				) {
					const heightDelta = Math.abs(height - lastEmittedDocSize.height);
					const widthDelta = Math.abs(width - lastEmittedDocSize.width);
					if (
						heightDelta < DOC_SIZE_DEDUP_HEIGHT_PX &&
						widthDelta < DOC_SIZE_DEDUP_WIDTH_PX
					) {
						return;
					}
				}

				lastEmittedDocSize = { width, height };
				const docSizeEvent = {
					type: 5,
					data: {
						tag: "iw-doc-size",
						payload: {
							tag: "doc-size",
							width,
							height,
							href: win.location.href,
							reason,
						},
					},
					timestamp: Date.now(),
					w_v: LIB_VERSION,
				};
				void addEvent(docSizeEvent);
			};

			const installDocSizeWatchers = () => {
				const win = getWindow();
				const doc = getDocument();
				if (!win || !doc) return;

				// Fresh session: discard previous baseline so the first observer /
				// poll check has nothing to dedup against.
				lastEmittedDocSize = null;
				emitDocSize("start");

				let debounceTimer: ReturnType<typeof setTimeout> | null = null;
				const scheduleObserverEmit = () => {
					if (debounceTimer) clearTimeout(debounceTimer);
					debounceTimer = setTimeout(() => {
						debounceTimer = null;
						emitDocSize("observer");
					}, DOC_SIZE_DEBOUNCE_MS);
				};

				let resizeObserver: ResizeObserver | null = null;
				let domContentLoadedHandler: (() => void) | null = null;

				const ResizeObserverCtor = (
					win as Window & {
						ResizeObserver?: typeof ResizeObserver;
					}
				).ResizeObserver;

				const attachResizeObserver = () => {
					if (typeof ResizeObserverCtor !== "function") return;
					try {
						resizeObserver = new ResizeObserverCtor(scheduleObserverEmit);
					} catch (e) {
						error("Failed to construct doc-size ResizeObserver", e);
						resizeObserver = null;
						return;
					}
					if (doc.documentElement) {
						try {
							resizeObserver.observe(doc.documentElement);
						} catch (e) {
							error("Failed to observe documentElement for doc-size", e);
						}
					}
					if (doc.body) {
						try {
							resizeObserver.observe(doc.body);
						} catch (e) {
							error("Failed to observe body for doc-size", e);
						}
					}
				};

				if (doc.body) {
					attachResizeObserver();
				} else {
					// body may not exist if SDK loads in <head> before parsing the
					// body; defer observation until DOMContentLoaded.
					domContentLoadedHandler = () => {
						domContentLoadedHandler = null;
						attachResizeObserver();
					};
					doc.addEventListener("DOMContentLoaded", domContentLoadedHandler, {
						once: true,
					});
				}

				const pollTimer = setInterval(() => {
					emitDocSize("poll");
				}, DOC_SIZE_POLL_INTERVAL_MS);

				docSizeCleanup = () => {
					if (debounceTimer) {
						clearTimeout(debounceTimer);
						debounceTimer = null;
					}
					if (resizeObserver) {
						try {
							resizeObserver.disconnect();
						} catch {
							// no-op
						}
						resizeObserver = null;
					}
					if (domContentLoadedHandler) {
						doc.removeEventListener(
							"DOMContentLoaded",
							domContentLoadedHandler,
						);
						domContentLoadedHandler = null;
					}
					clearInterval(pollTimer);
				};
			};

			const emitUrlChangeEvent = () => {
				const win = getWindow();
				if (!win) return;
				const url = win.location.href;
				if (url === lastRecordedUrl) return;
				lastRecordedUrl = url;
				const customEvent = {
					type: 5,
					data: {
						tag: "url-change",
						url,
						pathname: win.location.pathname,
						search: win.location.search,
						hash: win.location.hash,
					},
					timestamp: Date.now(),
					w_v: LIB_VERSION,
				};
				void addEvent(customEvent);
				// Route anchor: always emit doc-size, no dedup; updates the
				// baseline so subsequent observer / poll checks measure against
				// "height as of new page".
				emitDocSize("route");
			};

			const installUrlChangeListeners = () => {
				const win = getWindow();
				if (!win) return;
				const originalPushState = win.history.pushState.bind(win.history);
				const originalReplaceState = win.history.replaceState.bind(win.history);
				win.history.pushState = (
					...args: Parameters<typeof win.history.pushState>
				) => {
					originalPushState(...args);
					emitUrlChangeEvent();
				};
				win.history.replaceState = (
					...args: Parameters<typeof win.history.replaceState>
				) => {
					originalReplaceState(...args);
					emitUrlChangeEvent();
				};
				const popstateHandler = () => emitUrlChangeEvent();
				win.addEventListener("popstate", popstateHandler);
				urlChangeCleanup = () => {
					win.history.pushState = originalPushState;
					win.history.replaceState = originalReplaceState;
					win.removeEventListener("popstate", popstateHandler);
				};
			};

			const installErrorCapture = () => {
				if (!config.captureErrors) return;
				const win = getWindow();
				if (!win) return;

				const IW_SCRIPT_MARKER = "infinitewatch";
				const ERROR_RATE_MS = 500;
				let lastErrorAt = 0;
				let lastRejectionAt = 0;
				const __iw_lastReqErrorAt = new Map<string, number>();
				const pruneRateLimitMap = (map: Map<string, number>) => {
					if (map.size <= RATE_LIMIT_MAP_MAX) return;
					const cutoff = now() - REQ_ERROR_RATE_MS * 2;
					for (const [k, v] of map) {
						if (v < cutoff) map.delete(k);
					}
					if (map.size > RATE_LIMIT_MAP_MAX) {
						const excess = map.size - RATE_LIMIT_MAP_MAX;
						const iter = map.keys();
						for (let i = 0; i < excess; i++) {
							const key = iter.next().value;
							if (key !== undefined) map.delete(key);
						}
					}
				};

				const isIwScript = (filename?: string | null) => {
					if (!filename) return false;
					const lower = filename.toLowerCase();
					return (
						lower.includes(IW_SCRIPT_MARKER) ||
						lower.includes("rrweb") ||
						lower.includes("watch.js")
					);
				};

				const errorHandler = (ev: ErrorEvent) => {
					try {
						if (isIwScript(ev.filename)) return;
						const ts = now();
						if (ts - lastErrorAt < ERROR_RATE_MS) return;
						lastErrorAt = ts;
						const err = ev.error;
						const event: IwCustomEvent<IwJsErrorPayload> = {
							type: 5,
							data: {
								tag: "iw-js-error",
								payload: {
									message: ev.message || String(err),
									type: err?.name || "Error",
									filename: ev.filename || undefined,
									lineno: ev.lineno || undefined,
									colno: ev.colno || undefined,
									stack: err?.stack
										? String(err.stack).slice(0, 4096)
										: undefined,
								},
							},
							timestamp: ts,
						};
						void addEvent(event);
					} catch (e) {
						error("error handler failed", e);
					}
				};

				const rejectionHandler = (ev: PromiseRejectionEvent) => {
					try {
						const ts = now();
						if (ts - lastRejectionAt < ERROR_RATE_MS) return;
						lastRejectionAt = ts;
						const reason = ev.reason;
						const message =
							reason instanceof Error
								? reason.message
								: typeof reason === "string"
									? reason
									: "Unhandled promise rejection";
						const stack =
							reason instanceof Error
								? reason.stack?.slice(0, 4096)
								: undefined;
						const event: IwCustomEvent<IwJsErrorPayload> = {
							type: 5,
							data: {
								tag: "iw-js-error",
								payload: {
									message,
									type:
										reason instanceof Error
											? reason.name
											: "UnhandledRejection",
									stack,
								},
							},
							timestamp: ts,
						};
						void addEvent(event);
					} catch (e) {
						error("rejection handler failed", e);
					}
				};

				const originalFetch =
					typeof win.fetch === "function" ? win.fetch.bind(win) : null;
				const patchedFetch: typeof fetch = (input, init?) => {
					if (!originalFetch)
						return Promise.reject(new Error("fetch unavailable"));
					const startTs = now();
					let method: string;
					let url: string;
					if (input instanceof Request) {
						url = input.url;
						method = (init?.method || input.method || "GET").toUpperCase();
					} else {
						url = String(input);
						method = (init?.method || "GET").toUpperCase();
					}
					return originalFetch(input, init).then(
						(response: Response) => {
							if (response.status >= 400) {
								try {
									if (!iwIsBlockedHost(url)) {
										const key = `${method}:${iwKeyForRequest(url)}`;
										const lastSent = __iw_lastReqErrorAt.get(key) || 0;
										const ts = now();
										if (ts - lastSent >= REQ_ERROR_RATE_MS) {
											__iw_lastReqErrorAt.set(key, ts);
											pruneRateLimitMap(__iw_lastReqErrorAt);
											const ct = response.headers?.get("content-type") || "";
											const isTextBody =
												ct.startsWith("text/") ||
												ct.includes("application/json") ||
												ct.includes("application/xml");
											const cloned = isTextBody ? response.clone() : null;
											(cloned ? cloned.text() : Promise.resolve(undefined))
												.then((body) => {
													const event: IwCustomEvent<IwRequestErrorPayload> = {
														type: 5,
														data: {
															tag: "iw-request-error",
															payload: {
																url,
																method,
																status: response.status,
																statusText: response.statusText || undefined,
																duration_ms: now() - startTs,
																response_preview:
																	body?.slice(0, 512) || undefined,
															},
														},
														timestamp: ts,
													};
													void addEvent(event);
												})
												.catch(() => {
													const event: IwCustomEvent<IwRequestErrorPayload> = {
														type: 5,
														data: {
															tag: "iw-request-error",
															payload: {
																url,
																method,
																status: response.status,
																statusText: response.statusText || undefined,
																duration_ms: now() - startTs,
															},
														},
														timestamp: now(),
													};
													void addEvent(event);
												});
										}
									}
								} catch (e) {
									error("fetch interceptor failed", e);
								}
							}
							return response;
						},
						(err) => {
							try {
								if (!iwIsBlockedHost(url)) {
									const key = `${method}:${iwKeyForRequest(url)}`;
									const lastSent = __iw_lastReqErrorAt.get(key) || 0;
									const ts = now();
									if (ts - lastSent >= REQ_ERROR_RATE_MS) {
										__iw_lastReqErrorAt.set(key, ts);
										pruneRateLimitMap(__iw_lastReqErrorAt);
										const message =
											err instanceof Error
												? err.message
												: "Network request failed";
										const event: IwCustomEvent<IwRequestErrorPayload> = {
											type: 5,
											data: {
												tag: "iw-request-error",
												payload: {
													url,
													method,
													status: 0,
													statusText: message,
													duration_ms: now() - startTs,
												},
											},
											timestamp: ts,
										};
										void addEvent(event);
									}
								}
							} catch (e) {
								error("fetch interceptor failed", e);
							}
							throw err;
						},
					);
				};

				const xhrAvailable = typeof XMLHttpRequest !== "undefined";
				const alreadyPatched =
					xhrAvailable && "__iw_patched" in XMLHttpRequest.prototype;
				const origXhrOpen =
					xhrAvailable && !alreadyPatched
						? XMLHttpRequest.prototype.open
						: null;
				const origXhrSend =
					xhrAvailable && !alreadyPatched
						? XMLHttpRequest.prototype.send
						: null;

				if (origXhrOpen && origXhrSend) {
					(
						XMLHttpRequest.prototype as unknown as Record<string, boolean>
					).__iw_patched = true;
					XMLHttpRequest.prototype.open = function (
						this: IwXhr,
						method: string,
						url: string,
						async = true,
						username?: string | null,
						password?: string | null,
					) {
						this.__iw_method = (method || "GET").toUpperCase();
						this.__iw_url = String(url ?? "");
						return origXhrOpen.call(
							this,
							method,
							url,
							async,
							username,
							password,
						);
					} as unknown as typeof XMLHttpRequest.prototype.open;

					const origSendFn = origXhrSend;
					XMLHttpRequest.prototype.send = function (
						this: IwXhr,
						body?: Document | XMLHttpRequestBodyInit | null,
					) {
						const xhrStart = now();
						const xhrUrl = this.__iw_url || "";
						const xhrMethod = this.__iw_method || "GET";
						this.addEventListener(
							"loadend",
							() => {
								try {
									const isError = this.status >= 400 || this.status === 0;
									if (isError && !iwIsBlockedHost(xhrUrl)) {
										const key = `${xhrMethod}:${iwKeyForRequest(xhrUrl)}`;
										const lastSent = __iw_lastReqErrorAt.get(key) || 0;
										const ts = now();
										if (ts - lastSent >= REQ_ERROR_RATE_MS) {
											__iw_lastReqErrorAt.set(key, ts);
											pruneRateLimitMap(__iw_lastReqErrorAt);
											const event: IwCustomEvent<IwRequestErrorPayload> = {
												type: 5,
												data: {
													tag: "iw-request-error",
													payload: {
														url: xhrUrl,
														method: xhrMethod,
														status: this.status,
														statusText:
															this.statusText ||
															(this.status === 0
																? "Network request failed"
																: undefined),
														duration_ms: now() - xhrStart,
														response_preview:
															this.responseText?.slice(0, 512) || undefined,
													},
												},
												timestamp: ts,
											};
											void addEvent(event);
										}
									}
								} catch (e) {
									error("XHR interceptor failed", e);
								}
							},
							{ once: true },
						);
						return origSendFn.call(this, body);
					} as unknown as typeof XMLHttpRequest.prototype.send;
				}

				win.addEventListener("error", errorHandler);
				win.addEventListener("unhandledrejection", rejectionHandler);
				if (originalFetch) {
					win.fetch = patchedFetch;
				}

				errorCaptureCleanup = () => {
					win.removeEventListener("error", errorHandler);
					win.removeEventListener("unhandledrejection", rejectionHandler);
					if (originalFetch) {
						win.fetch = originalFetch;
					}
					if (origXhrOpen && origXhrSend) {
						XMLHttpRequest.prototype.open = origXhrOpen;
						XMLHttpRequest.prototype.send = origXhrSend;
						delete (
							XMLHttpRequest.prototype as unknown as Record<string, boolean>
						).__iw_patched;
					}
					__iw_lastReqErrorAt.clear();
				};
			};

			const installDataLayerCapture = () => {
				if (!config.captureDataLayer) return;
				const win = getWindow();
				if (!win) return;
				const dlName = config.dataLayerName || "dataLayer";
				const rawAllowlist = config.dataLayerAllowlist || [];
				const captureAll = rawAllowlist.includes("*");
				const allowSet = new Set(rawAllowlist);

				const isPlainObject = (v: unknown): v is Record<string, unknown> =>
					typeof v === "object" &&
					v !== null &&
					!Array.isArray(v) &&
					Object.prototype.toString.call(v) === "[object Object]";

				const isJsonScalar = (v: unknown) =>
					v === null ||
					typeof v === "string" ||
					typeof v === "number" ||
					typeof v === "boolean";

				const filterEntry = (
					entry: unknown,
				): { name?: string; payload: Record<string, unknown> } | null => {
					if (!isPlainObject(entry)) return null;
					const out: Record<string, unknown> = {};
					for (const [key, value] of Object.entries(entry)) {
						if (!captureAll && !allowSet.has(key)) continue;
						if (
							isJsonScalar(value) ||
							isPlainObject(value) ||
							Array.isArray(value)
						) {
							out[key] = value;
						}
					}
					if (Object.keys(out).length === 0) return null;
					let json: string;
					try {
						json = JSON.stringify(out);
					} catch {
						return null;
					}
					if (json.length > IW_DATALAYER_MAX_PAYLOAD_BYTES) {
						// Drop oversized entries rather than risk partial/garbled payloads.
						log(
							"InfiniteWatch dataLayer entry dropped (payload exceeds limit)",
							json.length,
						);
						return null;
					}
					const name =
						typeof entry.event === "string" && entry.event.trim().length > 0
							? entry.event.trim()
							: undefined;
					return { name, payload: out };
				};

				const emit = (entry: unknown) => {
					const filtered = filterEntry(entry);
					if (!filtered) return;
					const event = {
						type: 5,
						data: {
							tag: IW_DATALAYER_TAG,
							...(filtered.name ? { name: filtered.name } : {}),
							payload: filtered.payload,
						},
						timestamp: now(),
					};
					void addEvent(event);
				};

				// biome-ignore lint/suspicious/noExplicitAny: dataLayer is a global of arbitrary shape
				const winRec = win as unknown as Record<string, any>;
				const existing = winRec[dlName];
				const target: unknown[] = Array.isArray(existing) ? existing : [];
				if (!Array.isArray(existing)) {
					winRec[dlName] = target;
				}

				// Replay any items already present so we don't miss early pushes.
				for (const entry of target) emit(entry);

				const originalPush = target.push.bind(target);
				// biome-ignore lint/suspicious/noExplicitAny: array push signature
				const patchedPush = (...items: any[]) => {
					const result = originalPush(...items);
					for (const item of items) {
						try {
							emit(item);
						} catch (e) {
							log("InfiniteWatch dataLayer emit failed", e);
						}
					}
					return result;
				};
				try {
					(target as { push: typeof patchedPush }).push = patchedPush;
				} catch (e) {
					log("InfiniteWatch dataLayer push patch failed", e);
					return;
				}

				dataLayerCaptureCleanup = () => {
					try {
						(target as { push: typeof originalPush }).push = originalPush;
					} catch {
						// best-effort — if the host page rebuilt the array, leave it alone
					}
				};
			};

			const recordOptions: Record<string, unknown> = {
				emit: (event: eventWithTime) => {
					if (firstRrwebEventType == null) {
						firstRrwebEventType = event?.type ?? null;
					}
					const isSnapshot = event?.type === 2;
					if (isSnapshot) {
						snapshotEmitCount += 1;
						try {
							rebuildInteractiveIdMapFromFullSnapshot(event);
						} catch (e) {
							const name = e instanceof Error ? e.name : "Unknown";
							const message = e instanceof Error ? e.message : String(e);
							snapshotRebuildError = `${name}: ${message}`;
							// don't bail — let the snapshot continue down the pipeline
						}
					}
					const filtered = filterEvent(event);
					if (isSnapshot) {
						if (filtered) {
							snapshotFilterResult = "passed";
						} else {
							snapshotFilterResult =
								fullSnapshotFilteredReason === "idle_filter"
									? "dropped_idle"
									: "dropped_other";
						}
					}
					if (filtered) {
						if (filtered.type === 2) {
							fullSnapshotSeen = true;
						}
						// Optional: let host page observe events (e.g. window.__IW_CONFIG__.onEvent)
						try {
							const win = getWindow();
							const iwConfig = win
								? (
										win as Window & {
											__IW_CONFIG__?: {
												onEvent?: (e: unknown) => void;
											};
										}
									).__IW_CONFIG__
								: undefined;
							if (typeof iwConfig?.onEvent === "function") {
								iwConfig.onEvent(filtered);
							}
						} catch {
							// no-op
						}
						(filtered as eventWithTime & { w_v?: string }).w_v = LIB_VERSION;
						// In debug mode skip packing so events are readable in Network tab
						let eventToAdd: unknown;
						try {
							eventToAdd = config.debug ? filtered : packFn(filtered);
							if (filtered.type === 2) {
								snapshotPackOk = true;
							}
						} catch (e) {
							eventToAdd = filtered;
							if (filtered.type === 2) {
								snapshotPackOk = false;
								const name = e instanceof Error ? e.name : "Unknown";
								const message = e instanceof Error ? e.message : String(e);
								snapshotPackError = `${name}: ${message}`;
							}
						}
						if (filtered.type === 2) {
							// Instrument the snapshot's path through addEvent so we know
							// exactly which guard dropped it if it doesn't make it through.
							const addEventStartedAt = now();
							addEvent(eventToAdd).then((result) => {
								snapshotBufferResult = result;
								snapshotBufferTimingMs = now() - addEventStartedAt;
								if (result === "pushed") {
									snapshotPendingIngest = true;
								}
							});
						} else {
							void addEvent(eventToAdd);
						}
						// Type 5 session-started only when lastRecordedUrl not set yet (first full snapshot of session)
						if (filtered.type === 2 && !lastRecordedUrl) {
							const win = getWindow();
							const url = win?.location?.href ?? "";
							lastRecordedUrl = url;
							const sessionStartedEvent = {
								type: 5,
								data: {
									tag: "session-started",
									url,
									pathname: win?.location?.pathname ?? "",
									search: win?.location?.search ?? "",
									hash: win?.location?.hash ?? "",
									...buildSessionStartedAttribution(),
								},
								timestamp: Date.now(),
								w_v: LIB_VERSION,
							};
							void addEvent(sessionStartedEvent);
						}
					} else if (
						event?.type === 2 &&
						fullSnapshotFilteredReason !== "none"
					) {
						emitCaptureDiagnostics(null);
					}
				},
				maskAllInputs: config.maskAllInputs,
				maskInputOptions: config.maskInputOptions,
				recordCanvas: config.recordCanvas,
				recordIframe: config.recordIframe,
				recordCrossOriginIframes: config.recordCrossOriginIframes,
				collectFonts: config.collectFonts,
				inlineStylesheet: config.inlineStylesheet,
				slimDOMOptions: {
					script: true,
					comment: true,
					headFavicon: true,
					headWhitespace: true,
					headMetaSocial: true,
					headMetaRobots: true,
					headMetaHttpEquiv: true,
					headMetaVerification: true,
				},
				sampling: {
					mousemove: 300,
					scroll: 300,
					media: 3000,
					input: "last",
				},
				ignoreMutation: (m: MutationRecord) => {
					const t = m.target || null;
					if (!t || t.nodeType !== 1) return false;
					if (m.type === "attributes" && m.attributeName === "style") {
						const oldStyle = m.oldValue ?? "";
						const newStyle = (t as Element).getAttribute("style") ?? "";
						if (
							onlyAnimationStyleChanged(oldStyle, newStyle) &&
							!isInteractiveEl(t as Element)
						) {
							return true;
						}
					}
					if (
						m.type === "childList" &&
						m.addedNodes.length === 0 &&
						m.removedNodes.length === 0
					) {
						return true;
					}
					return false;
				},
			};

			if (config.blockClass) {
				recordOptions.blockClass = config.blockClass;
			}
			if (config.blockSelector) {
				recordOptions.blockSelector = config.blockSelector;
			}
			if (config.maskTextSelector) {
				recordOptions.maskTextSelector = config.maskTextSelector;
			}
			if (config.maskTextClass) {
				recordOptions.maskTextClass = config.maskTextClass;
			}

			iwInstallUserIntentListenersOnce();
			// Inject DOM mask classes for "All" mode with unmask selectors.
			// Must happen before rrweb.record() so the initial snapshot sees the classes.
			if (allModeUnmaskSelectors.length > 0) {
				const doc = getDocument();
				if (doc) {
					textMaskCleanup = applyAllModeTextMasking(
						doc,
						allModeUnmaskSelectors,
					);
				}
			}
			if (config.computeVisibility) {
				const doc = getDocument();
				if (doc?.documentElement) {
					// Stamp the entire document synchronously BEFORE rrweb takes its
					// FullSnapshot. This guarantees the snapshot the backend receives
					// already carries `data-iw-hidden` on every element, so the
					// preprocess can inject the right inline `display:none` and the
					// chunker treats the elements as hidden from chunk 1 instead of
					// silently importing them as visible. The cost is a synchronous
					// `getComputedStyle` walk on opt-in orgs only (controlled by
					// `computeVisibility`); customers without the flag pay nothing.
					tagSubtree(doc);
				}
			}
			stopRecording = rrweb.record(recordOptions);
			if (config.computeVisibility) {
				const doc = getDocument();
				if (doc?.documentElement) {
					if (visibilityTagCleanup) {
						visibilityTagCleanup();
						visibilityTagCleanup = null;
					}
					// Mutation observer keeps the tags in sync as the host page changes.
					// `tagSubtreeIdle` continues to run as a safety net: any element
					// that was added between the synchronous walk above and the observer
					// install below would otherwise miss its initial tag.
					const observerCleanup = installVisibilityObserver(doc);
					const walkCleanup = tagSubtreeIdle(doc);
					visibilityTagCleanup = () => {
						observerCleanup();
						walkCleanup();
					};
				}
			}
			installUrlChangeListeners();
			// NB: don't pre-emit a url-change here — emitUrlChangeEvent sets
			// lastRecordedUrl, which would suppress the type-5 `session-started`
			// event the recorder fires on the first full snapshot. The initial
			// URL is captured by `session-started`; url-change listeners handle
			// subsequent SPA/popstate navigations.
			if (docSizeCleanup) {
				docSizeCleanup();
				docSizeCleanup = null;
			}
			try {
				installDocSizeWatchers();
			} catch (e) {
				error("doc-size install failed, recording continues", e);
			}
			try {
				installErrorCapture();
			} catch (e) {
				error("Error capture install failed, recording continues", e);
			}
			try {
				installDataLayerCapture();
			} catch (e) {
				error("dataLayer capture install failed, recording continues", e);
			}
			transportBlocked = false;
			initSessionRecording();
		} catch (err) {
			const errName = err instanceof Error ? err.name : "UnknownError";
			const errMessage = err instanceof Error ? err.message : String(err);
			startRecordingError = `${errName}: ${errMessage}`;
			if (fullSnapshotFilteredReason === "none") {
				fullSnapshotFilteredReason = "start_recording_failed";
			}
			isSessionActive = false;
			transportBlocked = true;
			error("Failed to start recording:", err);
			emitCaptureDiagnostics(null);
		}
	};

	const initSessionRecording = () => {
		startSessionHeartbeat();
		startSessionExpiryTimer();
		saveSessionToStorage();
		if (config.minSessionDuration > 0) {
			minSessionFlushTimer = setTimeout(() => {
				minSessionFlushTimer = null;
				flushEvents();
				startFlushTimer();
			}, config.minSessionDuration);
		} else {
			startFlushTimer();
		}
	};

	const stopRecordingSession = () => {
		stopSessionHeartbeat();
		stopSessionExpiryTimer();
		stopFlushTimer();
		if (minSessionFlushTimer) {
			clearTimeout(minSessionFlushTimer);
			minSessionFlushTimer = null;
		}
		if (textMaskCleanup) {
			textMaskCleanup();
			textMaskCleanup = null;
		}
		if (snapshotFlushTimeout) {
			clearTimeout(snapshotFlushTimeout);
			snapshotFlushTimeout = null;
		}
		if (sessionStartTime !== null && config.minSessionDuration > 0) {
			if (now() - sessionStartTime < config.minSessionDuration) {
				eventBuffer = [];
			}
		}
		flushEvents();
		clearSessionFromStorage();
		if (urlChangeCleanup) {
			urlChangeCleanup();
			urlChangeCleanup = null;
		}
		if (docSizeCleanup) {
			docSizeCleanup();
			docSizeCleanup = null;
		}
		lastEmittedDocSize = null;
		if (dynamicStyleObserver) {
			dynamicStyleObserver.disconnect();
			dynamicStyleObserver = null;
		}
		if (visibilityTagCleanup) {
			visibilityTagCleanup();
			visibilityTagCleanup = null;
		}
		if (errorCaptureCleanup) {
			errorCaptureCleanup();
			errorCaptureCleanup = null;
		}
		if (dataLayerCaptureCleanup) {
			dataLayerCaptureCleanup();
			dataLayerCaptureCleanup = null;
		}
		if (filterUrlCleanup && !urlFilterPaused) {
			filterUrlCleanup();
		}
		lastRecordedUrl = "";
		sessionId = null;
		sessionStartTime = null;
		isSessionActive = false;
		resetCaptureDiagnosticsState();
	};

	const flushPendingCalls = () => {
		if (pendingIdentify) {
			const userIdentifier = pendingIdentify;
			pendingIdentify = null;
			identify(userIdentifier);
		}
		if (hardStop) {
			pendingSessionTags = [];
			pendingCustomEvents = [];
			return;
		}
		const tagsToSend = [...pendingSessionTags];
		pendingSessionTags = [];
		for (const payload of tagsToSend) {
			tagSession(payload);
		}
		const customEventsToSend = [...pendingCustomEvents];
		pendingCustomEvents = [];
		for (const payload of customEventsToSend) {
			trackCustomEvent(payload);
		}
	};

	const init = async (options: WebCoreOptions) => {
		if (!isBrowser()) {
			return null;
		}
		const resolvedOptions = applyBaseUrlToOptions(options);
		if (isInitialized) {
			log("Tracker already initialized");
			return client;
		}
		if (!resolvedOptions.organizationId) {
			// Always log this critical error regardless of debug setting
			console.error(
				"[InfiniteWatch]",
				"organizationId is required. Provide it as a prop/parameter or via an environment variable (e.g. NEXT_PUBLIC_INFINITEWATCH_ORG_ID or VITE_INFINITEWATCH_ORG_ID).",
			);
			return null;
		}
		if (isOrganizationExcluded(resolvedOptions.organizationId)) {
			log(
				"Organization excluded from tracking:",
				resolvedOptions.organizationId,
			);
			return null;
		}
		const initialConfig = { ...DEFAULT_CONFIG, ...resolvedOptions };
		let recordingConfig: Partial<typeof DEFAULT_CONFIG> = {};
		if (initialConfig.endpointConfig && initialConfig.organizationId) {
			try {
				const response = await fetch(initialConfig.endpointConfig, {
					method: "GET",
					headers: {
						"X-Organization": initialConfig.organizationId,
					},
					credentials: "omit",
					keepalive: false,
				});
				if (response.status === 403) {
					const errorData = await response.json();
					contractStatus = {
						error: errorData.error || "No active contract",
						message: errorData.message || "Your contract has expired",
						contract_expired: errorData.contract_expired || true,
					};
					log(
						"Contract expired or no active contract:",
						contractStatus.message,
					);
					return null;
				} else if (response.status === 429) {
					const errorData = await response.json();
					quotaStatus = {
						error: errorData.error || "Quota exceeded",
						message: errorData.message || "You have reached your usage limit",
						usage: errorData.usage || null,
						upgrade_url: errorData.upgrade_url || null,
					};
					log("Quota exceeded:", quotaStatus.message);
					return null;
				} else if (response.ok) {
					const configData = await response.json();
					const booleanFromConfig = (value: unknown, fallback: boolean) => {
						if (value == null) return fallback;
						if (value === "true") return true;
						if (value === "false") return false;
						return Boolean(value);
					};
					recordingConfig = {
						baseUrl: configData.baseUrl ?? initialConfig.baseUrl,
						endpoint: configData.endpoint ?? initialConfig.endpoint,
						endpointInsights:
							configData.endpointInsights ?? initialConfig.endpointInsights,
						endpointConfig:
							configData.endpointConfig ?? initialConfig.endpointConfig,
						endpointErrors:
							configData.endpointErrors ?? initialConfig.endpointErrors,
						defaultSamplingPercent:
							configData.samplingPercent ??
							initialConfig.defaultSamplingPercent,
						autoResumeSession: booleanFromConfig(
							configData.autoResumeSession,
							initialConfig.autoResumeSession,
						),
						batchSize: configData.batchSize ?? initialConfig.batchSize,
						flushInterval:
							configData.flushInterval ?? initialConfig.flushInterval,
						minSessionDuration:
							configData.minSessionDuration ?? initialConfig.minSessionDuration,
						sessionTimeout:
							configData.sessionTimeout ?? initialConfig.sessionTimeout,
						sessionHeartbeatInterval:
							configData.sessionHeartbeatInterval ??
							initialConfig.sessionHeartbeatInterval,
						inlineStylesheet: booleanFromConfig(
							configData.inlineStylesheet,
							initialConfig.inlineStylesheet,
						),
						collectFonts: booleanFromConfig(
							configData.collectFonts,
							initialConfig.collectFonts,
						),
						computeVisibility: booleanFromConfig(
							configData.computeVisibility,
							initialConfig.computeVisibility,
						),
						generateInsights: booleanFromConfig(
							configData.generateInsights,
							initialConfig.generateInsights,
						),
						blackList: Array.isArray(configData.blackList)
							? configData.blackList
							: initialConfig.blackList,
					};
					resolvedOptions.baseUrl = recordingConfig.baseUrl;
					resolvedOptions.endpoint = recordingConfig.endpoint;
					resolvedOptions.endpointInsights = recordingConfig.endpointInsights;
					resolvedOptions.endpointConfig = recordingConfig.endpointConfig;
					resolvedOptions.endpointErrors = recordingConfig.endpointErrors;
					resolvedOptions.defaultSamplingPercent =
						recordingConfig.defaultSamplingPercent;
					resolvedOptions.autoResumeSession = recordingConfig.autoResumeSession;
					resolvedOptions.batchSize = recordingConfig.batchSize;
					resolvedOptions.flushInterval = recordingConfig.flushInterval;
					resolvedOptions.minSessionDuration =
						recordingConfig.minSessionDuration;
					resolvedOptions.sessionTimeout = recordingConfig.sessionTimeout;
					resolvedOptions.sessionHeartbeatInterval =
						recordingConfig.sessionHeartbeatInterval;
					resolvedOptions.inlineStylesheet = recordingConfig.inlineStylesheet;
					resolvedOptions.collectFonts = recordingConfig.collectFonts;
					resolvedOptions.computeVisibility = recordingConfig.computeVisibility;
					resolvedOptions.generateInsights = recordingConfig.generateInsights;
					resolvedOptions.blackList = recordingConfig.blackList;
					if (
						configData.session_filters &&
						typeof configData.session_filters === "object"
					) {
						sessionFilters = parseSessionFilters(configData.session_filters);
						log("Session filters loaded:", JSON.stringify(sessionFilters));
					}
					// Resolve masking rules: prefer new maskingRules format, fall back to legacy maskSelectors
					let resolvedRules: Array<{
						selector: string;
						mode: "mask" | "unmask";
					}> = [];
					if (
						Array.isArray(configData.maskingRules) &&
						configData.maskingRules.length > 0
					) {
						resolvedRules = configData.maskingRules;
					} else if (
						Array.isArray(configData.maskSelectors) &&
						configData.maskSelectors.length > 0
					) {
						resolvedRules = configData.maskSelectors.map((s: string) => ({
							selector: s,
							mode: "mask" as const,
						}));
					}
					resolvedOptions.maskingRules = resolvedRules;

					const isValidSelector = (sel: string): boolean => {
						if (!sel || typeof sel !== "string" || !sel.trim()) return false;
						const doc = getDocument();
						if (!doc) return false;
						try {
							doc.querySelector(sel);
							return true;
						} catch {
							log("Skipping invalid maskingRule selector:", sel);
							return false;
						}
					};

					const maskSelectors = resolvedRules
						.filter((r) => r.mode === "mask")
						.map((r) => r.selector)
						.filter(isValidSelector);
					const unmaskSelectors = resolvedRules
						.filter((r) => r.mode === "unmask")
						.map((r) => r.selector)
						.filter(isValidSelector);

					const effectiveMasking = configData.masking;
					switch (effectiveMasking) {
						case "All": {
							resolvedOptions.maskAllInputs = true;
							if (unmaskSelectors.length > 0) {
								// maskTextSelector "*:not(sel)" does NOT work with rrweb: needMaskingText
								// calls el.closest(selector), which always finds a non-matching ancestor
								// (e.g. <body>), so the :not() exemption is always overridden.
								// Instead, inject a CSS class onto elements that should be masked and
								// use maskTextClass so rrweb only masks elements we explicitly mark.
								resolvedOptions.maskTextClass = UNMASK_CSS_CLASS;
								allModeUnmaskSelectors = unmaskSelectors;
							} else {
								resolvedOptions.maskTextSelector =
									resolvedOptions.maskTextSelector
										? `${resolvedOptions.maskTextSelector},*`
										: "*";
								allModeUnmaskSelectors = [];
							}
							break;
						}
						case "AllInputs":
							resolvedOptions.maskAllInputs = true;
							break;
						case "OnlyPasswords":
							resolvedOptions.maskAllInputs = false;
							resolvedOptions.maskInputOptions = {
								password: true,
								email: false,
								text: false,
								tel: false,
								url: false,
								textarea: false,
								select: false,
								checkbox: false,
								radio: false,
								file: false,
							};
							break;
					}

					if (effectiveMasking !== "All" && maskSelectors.length > 0) {
						const customSelectors = maskSelectors.join(",");
						resolvedOptions.maskTextSelector = resolvedOptions.maskTextSelector
							? `${resolvedOptions.maskTextSelector},${customSelectors}`
							: customSelectors;
					}
				} else {
					log("Config endpoint returned error:", response.status);
				}
			} catch (err) {
				log("Failed to fetch endpoint config, using defaults:", err);
			}
		}

		config = { ...DEFAULT_CONFIG, ...compactOptions(resolvedOptions) };

		if (config.autoResumeSession) {
			const existingSession = loadSessionFromStorage();
			const isFullSession =
				existingSession &&
				"sessionId" in existingSession &&
				"lastActivity" in existingSession &&
				existingSession.sessionId != null &&
				existingSession.lastActivity != null &&
				!isSessionExpired(existingSession);
			if (isFullSession) {
				sessionId = existingSession.sessionId;
				userId = existingSession.userId || config.userId || generateUserId();
				externalId = existingSession.externalId || null;
				organizationId = config.organizationId;
				sessionStartTime = existingSession.sessionStartTime || now();
				isSampledIn = shouldSample(organizationId, sessionId);
				if (config.captureAttribution) {
					if (existingSession.attribution) {
						// Same session continuing on a new page — keep its entry attribution.
						sessionAttribution = existingSession.attribution;
					} else {
						// Legacy resumed session without stored attribution: the current
						// page is mid-session, so don't treat it as a campaign entry.
						beginNewSessionAttribution({ allowLastTouch: false });
					}
				}
				if (!existingSession.userId) {
					saveSessionToStorage();
				}
			} else {
				ensureSessionContext();
			}
		} else {
			ensureSessionContext();
		}

		if (isSampledIn) {
			startSessionExpiryTimer();
		} else {
			log("Session not sampled in; not starting expiry timer");
		}

		isInitialized = true;
		flushPendingCalls();

		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "visible") {
				// Check if session has expired while tab was backgrounded
				if (isSessionActive && sessionStartTime) {
					const totalSessionDuration = now() - sessionStartTime;
					if (totalSessionDuration > config.sessionTimeout) {
						log("Session expired while tab was backgrounded, stopping session");
						stopRecordingSession();
						return; // Don't update activity for expired session
					}
				}
				updateSessionActivity();
			} else if (document.visibilityState === "hidden") {
				flushEvents(true);
				updateSessionActivity();
				void generateInsights().catch(() => {
					// swallow
				});
			}
		});

		return client;
	};

	const start = async () => {
		if (hardStop) {
			log("Recording blocked - hard stop is active");
			return client;
		}
		if (!isInitialized) {
			error("Tracker not initialized. Call init() first.");
			return client;
		}
		if (contractStatus) {
			log("Recording blocked - Contract issue:", contractStatus.message);
			return client;
		}
		if (quotaStatus) {
			log("Recording blocked - Quota exceeded:", quotaStatus.message);
			return client;
		}
		if (config.defaultSamplingPercent === 0) {
			log("Recording blocked - samplingPercent set to 0");
			return client;
		}
		if (!isSampledIn) {
			return client;
		}

		// Session filter: URL exclude check — defer, don't hard-stop
		if (isUrlExcluded()) {
			log("Recording deferred - current URL matches exclude filter");
			urlFilterPaused = true;
			setupUrlFilterMonitoring();
			return client;
		}

		// Session filter: auth_filter === "signed_in" defers recording until identify()
		if (sessionFilters?.auth_filter === "signed_in" && !externalId) {
			awaitingIdentify = true;
			log(
				"Recording deferred - waiting for identify() (auth_filter=signed_in)",
			);
			setupUrlFilterMonitoring();
			return client;
		}

		// Session filter: URL include check
		if (!isUrlIncluded()) {
			log("Recording deferred - current URL does not match include filter");
			setupUrlFilterMonitoring();
			return client;
		}

		// Set up URL monitoring for ongoing navigation changes
		if (
			sessionFilters?.exclude_urls?.length ||
			sessionFilters?.include_urls?.length
		) {
			setupUrlFilterMonitoring();
		}

		try {
			ensureSessionContext();
			await startRecording();
		} catch (err) {
			error("Failed to start recording:", err);
		}
		return client;
	};

	const stop = () => {
		stopRecordingSession();
		return client;
	};

	const hardStopSession = () => {
		hardStop = true;
		stopRecordingSession();
		return client;
	};

	const flush = (isUnloading = false) => {
		flushEvents(isUnloading);
		return client;
	};

	const identify = (userIdentifier: UserIdentifier) => {
		if (!userIdentifier || !userIdentifier.external_id) {
			error("InfiniteWatch.identify: external_id is required");
			return client;
		}
		if (hardStop) {
			log("InfiniteWatch.identify ignored because hard stop is active");
			return client;
		}
		if (!isInitialized || !sessionId || !userId) {
			pendingIdentify = userIdentifier;
			log("InfiniteWatch.identify queued until init creates a session");
			return client;
		}
		if (!organizationId) {
			organizationId = config.organizationId;
		}
		if (!organizationId) {
			error(
				"InfiniteWatch.identify: No organization ID set. Cannot identify user.",
			);
			return client;
		}

		/* Set externalId and persist to cookie immediately so session cookie has it before server responds */
		externalId = userIdentifier.external_id;
		saveSessionToStorage();

		// Session filter: internal user check
		if (isInternalEmail(userIdentifier.email)) {
			log("Internal user detected, stopping recording:", userIdentifier.email);
			urlFilterPaused = false;
			hardStop = true;
			stopRecordingSession();
			return client;
		}

		// Session filter: auth_filter === "anonymous" means stop when user identifies
		if (sessionFilters?.auth_filter === "anonymous") {
			log(
				"Authenticated user detected with anonymous-only filter, stopping recording",
			);
			urlFilterPaused = false;
			hardStop = true;
			stopRecordingSession();
			return client;
		}

		// Session filter: auth_filter === "signed_in" and we were waiting for identify
		if (awaitingIdentify && sessionFilters?.auth_filter === "signed_in") {
			awaitingIdentify = false;
			log(
				"User identified, auth_filter=signed_in satisfied, starting recording",
			);
			if (!isUrlExcluded() && isUrlIncluded()) {
				startRecording().catch((err) =>
					error("Failed to start recording after identify:", err),
				);
			}
		}

		const doc = getDocument();
		const win = getWindow();
		if (!win) {
			return client;
		}
		const nav =
			win.navigator ??
			(typeof navigator !== "undefined" ? navigator : undefined);
		const userLanguage =
			nav && "userLanguage" in nav
				? (nav as { userLanguage?: string }).userLanguage
				: undefined;
		const browserMetadata = {
			user_agent: nav?.userAgent,
			language: nav?.language || userLanguage,
			languages: nav?.languages ? Array.from(nav.languages) : [],
			platform: nav?.platform,
			screen_width: win.screen?.width,
			screen_height: win.screen?.height,
			viewport_width: win.innerWidth,
			viewport_height: win.innerHeight,
			color_depth: win.screen?.colorDepth,
			pixel_ratio: win.devicePixelRatio || 1,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			timezone_offset: new Date().getTimezoneOffset(),
			url: win.location?.href,
			pathname: win.location?.pathname,
			referrer: doc?.referrer || null,
			online: nav?.onLine,
			cookie_enabled: nav?.cookieEnabled,
			do_not_track: nav?.doNotTrack || null,
			touch_support: "ontouchstart" in win || (nav?.maxTouchPoints ?? 0) > 0,
		};
		const requestBody: Record<string, unknown> = {
			external_id: userIdentifier.external_id,
			session_id: sessionId,
			user_id: userId,
			organization_id: organizationId,
			client_version: LIB_VERSION,
			client_sdk: sdkName,
			client_metadata: browserMetadata,
		};
		for (const key in userIdentifier) {
			if (key !== "external_id" && Object.hasOwn(userIdentifier, key)) {
				requestBody[key] = userIdentifier[key];
			}
		}
		const payloadString = JSON.stringify(requestBody);
		const payloadSizeKB = new Blob([payloadString]).size / BYTES_IN_KILOBYTE;
		const endpointUrl = buildEndpointUrl(
			`${config.baseUrl}/v1/users`,
			"f",
			true,
			payloadSizeKB,
			false,
			sessionId,
		);
		fetch(endpointUrl, {
			method: "POST",
			headers: {
				"Content-Type": "text/plain",
			},
			body: payloadString,
			keepalive: true,
		})
			.then((response) => {
				if (response.status === 0) {
					log("Identify request aborted (status 0)");
				} else if (response.ok) {
					transportBlocked = false;
					externalId = userIdentifier.external_id;
					saveSessionToStorage();
				} else if (response.status >= 500) {
					error("Failed to identify user:", response.status);
				}
			})
			.catch((err) => {
				const errorMessage = (err as { message?: string })?.message || "";
				const isBlocked =
					errorMessage.includes("Failed to fetch") ||
					errorMessage.includes("NetworkError") ||
					errorMessage.includes("ERR_BLOCKED_BY_CLIENT") ||
					errorMessage.includes("ERR_BLOCKED_BY_RESPONSE") ||
					errorMessage.includes("net::ERR_FAILED");
				const isAborted =
					(err as { name?: string })?.name === "AbortError" ||
					(err as { name?: string })?.name === "TimeoutError" ||
					errorMessage.includes("aborted") ||
					errorMessage.includes("cancelled");
				if (isBlocked || isAborted) {
					transportBlocked = true;
				} else {
					error("InfiniteWatch identify failed:", err);
				}
			});
		return client;
	};

	const tagSession = (payload: SessionTagsPayload) => {
		if (!payload || !Array.isArray(payload.tags)) {
			error("InfiniteWatch.tagSession: tags must be an array of tag objects");
			return client;
		}
		if (hardStop) {
			log("InfiniteWatch.tagSession ignored because hard stop is active");
			return client;
		}
		if (!isInitialized || !sessionId || !organizationId) {
			pendingSessionTags.push(payload);
			log("InfiniteWatch.tagSession queued until init creates a session");
			return client;
		}

		const tags = payload.tags
			.filter(
				(t): t is SessionTag =>
					t != null &&
					typeof t === "object" &&
					typeof t.name === "string" &&
					t.name.trim().length > 0,
			)
			.map((t) => ({
				name: t.name.trim(),
				...(t.color ? { color: t.color } : {}),
			}));

		if (tags.length === 0) {
			return client;
		}

		const capturedSessionId = sessionId;
		const capturedOrganizationId = organizationId;

		const requestBody = {
			session_id: capturedSessionId,
			organization_id: capturedOrganizationId,
			tags,
		};
		const payloadString = JSON.stringify(requestBody);
		const payloadSizeKB = new Blob([payloadString]).size / BYTES_IN_KILOBYTE;
		const endpointUrl = buildEndpointUrl(
			`${config.baseUrl}/v1/sessions/${encodeURIComponent(capturedSessionId)}/tags`,
			"f",
			true,
			payloadSizeKB,
			false,
			capturedSessionId,
		);

		fetch(endpointUrl, {
			method: "POST",
			headers: { "Content-Type": "text/plain" },
			body: payloadString,
			keepalive: true,
		}).catch(() => {
			// swallow telemetry failures
		});

		log("Session tagged:", tags);
		return client;
	};

	const getSessionInfo = (): SessionInfo => {
		return {
			sessionId,
			userId,
			organizationId,
			sessionStartTime,
			isActive: isSessionActive,
			hasStoredSession: !!loadSessionFromStorage(),
		};
	};

	const trackCustomEvent = (payload: TrackCustomEventPayload) => {
		if (
			!payload ||
			typeof payload.tag !== "string" ||
			payload.tag.trim().length === 0
		) {
			error("InfiniteWatch.trackCustomEvent: tag is required");
			return client;
		}
		if (hardStop) {
			log("InfiniteWatch.trackCustomEvent ignored because hard stop is active");
			return client;
		}
		if (!isInitialized || !sessionId || !organizationId) {
			pendingCustomEvents.push(payload);
			log("InfiniteWatch.trackCustomEvent queued until init creates a session");
			return client;
		}

		const win = getWindow();
		const url = win?.location.href;
		const pathname = win?.location.pathname;
		const search = win?.location.search;
		const hash = win?.location.hash;

		const customEvent = {
			type: 5,
			data: {
				tag: payload.tag.trim(),
				...(url ? { url, pathname, search, hash } : {}),
				...(typeof payload.name === "string" && payload.name.trim().length > 0
					? { name: payload.name.trim() }
					: {}),
				...(payload.metadata && typeof payload.metadata === "object"
					? { metadata: payload.metadata }
					: {}),
			},
			timestamp: Date.now(),
			w_v: LIB_VERSION,
		};

		void addEvent(customEvent);
		return client;
	};

	const getSessionId = () => sessionId;

	const isBlocked = () => {
		return (
			contractStatus !== null ||
			quotaStatus !== null ||
			transportBlocked ||
			config.defaultSamplingPercent === 0 ||
			!isSampledIn
		);
	};

	const getContractStatus = () => contractStatus;
	const getQuotaStatus = () => quotaStatus;

	const client: WebCoreClient = {
		init,
		start,
		stop,
		hardStop: hardStopSession,
		flush,
		identify,
		tagSession,
		trackCustomEvent,
		generateInsights,
		getSessionId,
		getSessionInfo,
		isBlocked,
		getContractStatus,
		getQuotaStatus,
	};

	return client;
};
